import type { ReelTheme } from "./reelTheme.ts";
import type { SITE_TYPES_ARRAY } from "./constants.ts";
import type { IconName } from "./iconNames.ts";
import type { MailLane } from "./mailCapabilities.ts";
import type { PlanId } from "./plans.ts";

/**
 * ⚠ **DERIVED from `SITE_TYPES_ARRAY`, since round 244, because the hand-written union had drifted.**
 * It listed seven members while the array (which the zod schemas, the scraper's guard and every
 * `<option>` list read) had NINE - `newsletter` and `creator` had been added to one and not the
 * other. Nothing failed: a value typed `SiteType` simply could not be either of the two real
 * categories, so any exhaustive switch over it would have been wrong in the direction that
 * typechecks. **A union and an array of the same set is a fork whether or not either is wrong.**
 */
export type SiteType = (typeof SITE_TYPES_ARRAY)[number];

export interface StreamingLinks {
	spotify?: string;
	apple_music?: string;
	soundcloud?: string;
	deezer?: string;
	youtube?: string;
}

/**
 * ⚠ **The artist's own PROFILE on each service, which is not `StreamingLinks` above** - that one is
 * per RELEASE and is where a fan goes to hear this song. Both hold a `deezer` key and they mean
 * different pages; a value copied between them is a link that goes to the wrong place.
 */
export interface SocialLinks {
	tiktok?: string;
	instagram?: string;
	facebook?: string;
	youtube?: string;
	spotify?: string;
	apple_music?: string;
	soundcloud?: string;
	deezer?: string;
}

/** A genre-specific submission address, e.g. an outlet's indie pop editor */
export interface GenreEmailEntry {
	genre: string;
	email: string;
}

export interface Blog {
	id: number;
	name: string;
	url: string;
	/**
	 * The outlet's contact address.
	 *
	 * ⚠ **`null` on the wire in saas for every outlet this workspace did not create itself** (§AB
	 * round 2, `services/outletContact.ts`): a scraped editorial contact is the operator's asset, and
	 * the whole catalog was one authenticated fetch away before the mask. Selfhost is unchanged - the
	 * owner scraped those addresses into their own file. **Read `emailable`, never `email`, to ask
	 * whether an outlet can be pitched at all.**
	 */
	email: string | null;
	/**
	 * Whether the server holds a contact address for this outlet - the question the browser used to
	 * answer for itself by looking at `email`, and cannot once the address is masked (§AB round 2).
	 *
	 * ⚠ Absent on payloads that predate the mask; treat `undefined` as unknown and fall back to
	 * `email`. Present in BOTH editions, so no surface needs to know which one it is running in.
	 */
	emailable?: boolean;
	/** Genre-specific editor emails (JSON string in the DB, parsed array in API responses).
	 *  ⚠ Empty on the wire in saas for a curated outlet - a JSON column of `{ genres, email }` desks is
	 *  several addresses wearing a different key, and the genre-matched desk is picked server-side. */
	genre_emails?: GenreEmailEntry[] | string | null;
	/**
	 * The blog's own direct submission/contact form - its free, direct front door (round 60's
	 * "direct-submission detector"). Recorded for every blog that exposes one, alongside the editor
	 * email, not only for email-less "form-only" blogs (its original role). It is the cheaper,
	 * relationship-building alternative to a paid submission platform, surfaced beside the email.
	 */
	submission_form_url?: string | null;
	description: string | null;
	genre_focus: string | null;
	submission_guidelines: string | null;
	site_type?: SiteType | null;
	subject_format: string | null;
	language: string | null;
	country: string | null;
	local_only: number;
	accepted_regions: string | null;
	source_url: string | null;
	discovered_at: string;
	verified: number;
	active: number;
	acceptsSingles: boolean;
	/** The outlet has asked for payment (reply or scraped signals) - flag, not a block */
	pay_to_play?: number | null;
	/** The outlet has offered GUARANTEED streams/placement - a Spotify-ToS red flag (v78) */
	stream_guarantee?: number | null;
	disabled?: number;
	disabled_reason?: string;
	accepts_singles?: number;
	allowsFollowUps?: boolean;
	allows_follow_ups?: number;
	maxDaysBeforeRelease?: number | null;
	max_days_before_release?: number | null;
	// Engagement stats + 0–100 quality score (list endpoint only; null when never pitched)
	sent_count?: number;
	replied_count?: number;
	opened_count?: number;
	accepted_count?: number;
	score?: number | null;
	/** Relationship tier derived from history (list endpoint only) */
	tier?: "champion" | "responsive" | "contacted" | "new";
	// Response analytics extras (list endpoint only)
	rejected_count?: number;
	/** Median days between send and reply; null when they never replied */
	median_days_to_reply?: number | null;
	/** Most recent touch in either direction (sent/replied/accepted/rejected) */
	last_interaction?: string | null;
	/** Learned track-record adjustment (±15) and what it is made of (list endpoint only) */
	track_adjustment?: number | null;
	track_evidence?: TrackEvidenceCounts | null;
	/** Approved people on this outlet (list endpoint only) */
	editors_count?: number;
	/** Email deliverability: 'ok' | 'invalid' | 'no_mx' | 'unknown' | null (unchecked) */
	email_status?: string | null;
	email_checked_at?: string | null;
	/** Playlist curator discovery: follower count of the largest playlist */
	follower_count?: number | null;
	/** Playlist curators: the person behind the playlists (this is also `name`) */
	curator_name?: string | null;
	/** Playlist curators: every playlist we've found for them (JSON in the DB) */
	playlists?: string | null;
	/** Playlist trust audit (round 107): cached curator-level verdict, so a badge renders offline */
	trust_verdict?: TrustVerdict | null;
	trust_score?: number | null;
	/** Curator-level signals; a JSON string in the DB, parsed to an array by the blogs route */
	trust_signals?: TrustSignal[] | string | null;
	trust_checked_at?: string | null;
	/**
	 * WHOSE decision an already-`disabled` row is (§O, v121). The server folds a per-workspace
	 * switch-off into the same `disabled` flag the whole app already filters on - so `compose.ts`
	 * needs no change and, more to the point, cannot FORGET - and this distinction exists only for
	 * the Outlets page, which is where a switched-off row goes back on.
	 *
	 * ⚠ Absent means the catalog's own state, and that default is load-bearing in the same
	 * direction the coupling table's absence is: **not switched off by me** is the safe reading, and
	 * the unsafe one empties every tenant's list.
	 */
	disabled_by?: "workspace" | "catalog" | null;
	/**
	 * "Is this row mine to edit?" - answered on the SERVER because the browser does not know which
	 * workspace it is in, and `true` in selfhost for every row.
	 *
	 * ⚠ It drives which BUTTONS appear and nothing else. The guard that matters is on every mutating
	 * route (§O), because a hosted tenant who can still `DELETE /api/blogs/:id` deletes an outlet out
	 * of every other tenant's catalog - hiding a button is cosmetic.
	 */
	owned_by_workspace?: boolean;
}

/** AI song analysis, stored as JSON on the release */
export interface SongAnalysis {
	genre: string;
	subgenres: string[];
	moods: string[];
	instruments: string[];
	/** e.g. "male lead vocals with layered harmonies", "instrumental" */
	vocals: string;
	/** e.g. "mid-tempo, ~95 BPM" */
	tempo: string;
	/** e.g. "high-energy", "laid-back" */
	energy: string;
	/** One-paragraph sonic description usable in pitches */
	description: string;
	/** What the analysis was based on */
	source: "audio" | "lyrics";
}

/** One detected section of the release's audio (times in seconds from track start) */
export interface AudioSection {
	start: number;
	end: number;
	/** Relative signal score (energy × entry-rise); comparable within one track only */
	score: number;
	/**
	 * Musixmatch-style section category. The signal heuristic assigns a first guess;
	 * the studio dropdown lets the user recategorize. "drop" is legacy (pre-Musixmatch
	 * data) - still valid to store, no longer offered in the dropdown.
	 */
	kind: "intro" | "verse" | "pre-chorus" | "chorus" | "post-chorus" | "hook" | "bridge" | "outro" | "drop";
	/** Short human label from the AI pass (e.g. "Final chorus"), when labeling succeeded */
	label?: string;
	/**
	 * The one section the artist wants listeners dropped into: the EPK's "Hear the hook"
	 * button jumps here. Without it the strongest-scoring section is used, which is rarely
	 * the second chorus people actually want to lead with. At most one section per release.
	 */
	favorite?: boolean;
	/** The lines sung inside this window (from the synced lyrics), or AI-guessed mood lines */
	captions?: string[];
	/**
	 * True when the captions were hand-edited: derivation from synced lyrics then leaves
	 * them alone. Absent/false captions are fair game to recompute on boundary changes.
	 */
	captionsManual?: boolean;
}

/** Stored in releases.audio_sections as JSON */
export interface SectionAnalysis {
	/** Full track length, seconds */
	duration: number;
	/** 3-5 non-overlapping sections, sorted by start time */
	sections: AudioSection[];
}

/**
 * Stored in releases.beat_grid as JSON - the beat grid MEASURED from the uploaded master
 * (see services/beatTrack.ts), as opposed to the synthetic grid a bare BPM implies. A grid
 * built from a stored BPM has no phase and assumes 4/4; this one carries the actual beat
 * instants, so the reel's zoom punch lands on the downbeats the track really has.
 */
export interface BeatGridAnalysis {
	/** Analyzer version - bump it in shared/video.ts and stale grids re-analyze on next use */
	version: number;
	/** Analyzed, but no confident pulse was found (ambient, rubato, spoken word) - don't retry */
	none?: boolean;
	/** Median tempo of the tracked beats - measured, so it may disagree with the typed-in bpm */
	bpm?: number;
	/** Beats per bar as FELT: 4 = common time, 3 = waltz (or 6/8 felt in three), 5/7 = odd meters */
	meter?: number;
	/** 0-1: how sharply the pulse and its bar grouping stood out of the onset envelope */
	confidence?: number;
	/** Every tracked beat, seconds into the track */
	beats?: number[];
	/** The bar-starting beats among `beats` - what the end card cuts on */
	downbeats?: number[];
	/** How loud the track is across each beat, 0-1 on a dB ramp, positional over `beats` */
	strengths?: number[];
}

/** One lyric line with its tap-synced start time; t is null until the line is stamped */
export interface SyncedLyricLine {
	/** Seconds from track start when the line begins being sung; null = not stamped yet */
	t: number | null;
	text: string;
	/**
	 * Seconds when the line stops being sung, marked by hand when the song then pauses -
	 * the last word before an instrumental break would otherwise be assumed to ring on
	 * until the next line. Absent: the line runs to the next stamp.
	 */
	e?: number | null;
	/**
	 * Per-word start times, one slot per whitespace-separated word of `text`, in order;
	 * null = that word wasn't tapped. Word stamps are a *superset* of the line stamp - `t`
	 * is the first tapped word - so tapping words syncs the line for free and there is
	 * never a second pass to keep in step (see `lineStart`).
	 *
	 * Only starts are tapped: a word ends where the next one begins, and the last word of a
	 * line ends where the line does. So `e` stays the hand-marked line end it always was.
	 *
	 * The slots are positional, which makes them hostage to the lyric text. If the text is
	 * later edited so the word count no longer matches, the whole array is ignored rather
	 * than silently mapped onto the wrong words - word cues fall back to interpolation
	 * instead of drifting.
	 */
	w?: (number | null)[] | null;
}

/** Stored in releases.lyrics_synced as JSON - line-level timestamps, Musixmatch-style */
export interface SyncedLyrics {
	lines: SyncedLyricLine[];
}

/** Per-section alignment quality of the linked music video */
export interface VideoSyncSection {
	start: number;
	end: number;
	/** Local correlation at the global offset - low r means the video is re-cut here */
	r: number;
	ok: boolean;
}

/** Stored in releases.video_sync as JSON: how the music video maps onto the master */
export interface VideoSync {
	/** video_time = track_time + offset (seconds; negative when the video starts mid-song) */
	offset: number;
	/** Cross-correlation confidence 0-1 - below ~0.35 the alignment can't be trusted */
	confidence: number;
	videoDuration: number;
	checkedAt: string;
	sections?: VideoSyncSection[];
}

/** Uploaded release audio (MP3/WAV). Bytes live on disk; this is the DB/API metadata. */
/**
 * What a release's uploaded audio IS (round 186, v116).
 *
 * ⚠ **Every reader in this app that wants "the song" means `master`**, and until this existed it
 * could not say so - there was one file per format, so "the mp3" and "the master" were the same
 * sentence. The reel renderer's audio bed, the hook cut the smart link and the bio page play, the
 * beat grid, the press kit's download, the pre-flight check and the silent-page monitor are all
 * about the master and would all be silently wrong on an instrumental. `tests/audioKind.test.ts` is
 * the canary that keeps a new query from forgetting to say which one it wants.
 *
 * The `instrumental` exists for the sync lane: a supervisor's second question, after clearance, is
 * whether one is available, and a pitch that cannot answer it is deleted unread.
 */
export const AUDIO_KINDS = ["master", "instrumental"] as const;
export type AudioKind = (typeof AUDIO_KINDS)[number];

/**
 * The upload slots the editor actually offers (round 212, §I.5).
 *
 * ⚠ **This is NOT the cross product of `AUDIO_KINDS` and the two formats, and the omission is the
 * decision.** A format earns a slot from a READER, not from symmetry. WAV exists in this app because
 * the renderers want it - `hookCut`, `beatTrack`, `videoJobs` and `videoAlign` all sort
 * `format = 'wav' DESC` to cut and measure from the best bytes available - and every one of those is
 * about the master. Nothing in this app reads an instrumental WAV, and it cannot be delivered either:
 * 46 MB is far past what a press kit may deploy (the master's own WAV is an external link for exactly
 * that reason), and no mail lane here carries attachments. A fourth slot would be a knob with nothing
 * on the other end of it, which this app's own rule forbids - every knob is a promise.
 *
 * ⚠ **Order is the render order** of the editor's Audio section, so the master's two rows stay where
 * they have always been and the instrumental arrives underneath.
 */
export const AUDIO_SLOTS = [
	{ id: "master:mp3", kind: "master", format: "mp3" },
	{ id: "master:wav", kind: "master", format: "wav" },
	{ id: "instrumental:mp3", kind: "instrumental", format: "mp3" }
] as const satisfies readonly { id: string; kind: AudioKind; format: "mp3" | "wav" }[];

/** `"<kind>:<format>"` - the key the release editor holds one upload under. Derived, never listed. */
export type AudioSlotId = (typeof AUDIO_SLOTS)[number]["id"];

export interface AudioFile {
	id: number;
	/** NULL until the release is saved - uploads can happen before the release exists */
	release_id: number | null;
	format: "mp3" | "wav";
	/** Which recording this is. Existing rows are all `master` - see the v116 migration. */
	kind: AudioKind;
	original_name: string | null;
	size: number | null;
	created_at: string;
	/** Absolute download URL (set on API responses) */
	url?: string;
}

/** Artist press photo, grouped by aspect ratio for press kits */
export interface ArtistPhoto {
	id: number;
	artist_id: number;
	aspect: "square" | "portrait" | "landscape";
	created_at: string;
}

export interface Release {
	id: number;
	artist_id: number | null;
	artist_name: string;
	artist_avatar_image?: string | null;
	title: string;
	release_type: "single" | "ep" | "album";
	genre: string | null;
	/** Artist-entered mood/trait keywords, comma-separated (e.g. "energetic, cinematic, sad") */
	moods: string | null;
	/** Artist-entered instrumentation, comma-separated (e.g. "analog synths, live drums") */
	instruments: string | null;
	artist_origin: string | null;
	/** Where the artist is currently based (may differ from artist_origin) */
	current_location: string | null;
	promo_blurb_long: string | null;
	promo_blurb_short: string | null;
	about: string | null;
	artist_bio: string | null;
	artist_bio_short: string | null;
	artist_press_highlights: string | null;
	/** External press kit for this release (Drive folder, Notion page, …); plain URL or a locale map */
	press_kit_url: string | null;
	streaming_links: StreamingLinks | string | null;
	/** Spotify album id (from the S4A pre-release URI) - release-day auto-link + smart-link flip target */
	spotify_album_id?: string | null;
	social_links: SocialLinks | string | null;
	extra_links: string | null;
	ean_upc: string | null;
	isrc: string | null;
	release_date: string | null;
	recording_year: number | null;
	lyrics: string | null;
	pronouns?: string | null;
	explicit?: boolean | null;
	duration: string | null;
	bpm: number | null;
	album_title: string | null;
	composed_by: string | null;
	recorded_by: string | null;
	mixed_by: string | null;
	mastered_by: string | null;
	label: string | null;
	collaborators: string | null;
	/** Alternative content (live sessions, music videos…): JSON array of {type, title, url} */
	related_content: string | null;
	cover_image: string | null;
	producer: string | null;
	recording_equipment: string | null;
	recording_studio: string | null;
	/**
	 * §I.5's clearance, v126 - the three facts a music supervisor asks about before reading anything
	 * else. ⚠ `rights_one_stop` is `"yes" | "no" | null` and NOT a boolean: "nobody has said" is a real
	 * state and a different sentence to a supervisor than "no, there is a co-publisher" (round 198's
	 * `explicit` finding, in the lane it was pointing at). ⚠ `writer_splits` names PEOPLE and their
	 * shares, which is why it never reaches a published page - see `docs/notes/published-pages.md`.
	 */
	rights_one_stop?: "yes" | "no" | null;
	publisher?: string | null;
	writer_splits?: string | null;
	/**
	 * Whether the uploaded instrumental is offered as a download on the PUBLISHED press kit (v127).
	 * 0 by default and only ever 1 because somebody said so: the page is public, so the file is a free
	 * karaoke bed and remix source forever once it is up. Holding an instrumental for the pitch and
	 * publishing one are two different decisions, and this is the second.
	 */
	instrumental_on_epk?: number;
	created_at: string;
	download_mp3_url?: string | null;
	download_wav_url?: string | null;
	/** Private pre-release stream (SoundCloud secret link etc.) for press */
	promo_stream_url?: string | null;
	/** Netlify site backing the published press kit (created once, re-deployed in place) */
	epk_site_id?: string | null;
	/**
	 * Is the press kit on the workspace hub? ⚠ **`*_on_hub` is what the app INTENDED and `*_url` is
	 * what a deploy actually returned** - both are needed to say a stranger can reach it, which is why
	 * `shared/publishedSurfaces.ts` is the one place that judges. They were absent from this interface
	 * until 2026-08-25 while being on the wire the whole time, so every frontend reader that wanted to
	 * know whether something was live had to guess from the URL alone.
	 */
	epk_on_hub?: number | null;
	/** Public URL of the published press kit; stable across re-publishes */
	epk_url?: string | null;
	/** Locale pages the last EPK publish deployed (JSON ["da","de"] - sibling <code>.html files) */
	epk_locales?: string | null;
	epk_published_at?: string | null;
	/** Which press kit {{epk_url}} points at in outreach; "published" falls back to external */
	epk_link_mode?: "published" | "external" | null;
	/** Per-release EPK appearance (JSON {accent?, font?}); null = app defaults */
	epk_theme?: string | { accent?: string | null; font?: string | null } | null;
	/**
	 * Per-release FAN KIT appearance (JSON {accent?}); v119.
	 *
	 * ⚠ Its `accent` accepts the literal `"auto"` beside a hex, and an absent value means AUTO here
	 * while the same absence on `epk_theme` means the workspace theme color. That asymmetry is
	 * deliberate and is written down once, in `services/surfaceAccent.ts` - a default is a promise to
	 * the pages already published, and these two surfaces made different promises.
	 */
	kit_theme?: string | { accent?: string | null } | null;
	/** Netlify site backing the published smart-link page (created once, re-deployed in place) */
	link_site_id?: string | null;
	/** Is the smart link on the hub? See `epk_on_hub` above on why both halves are needed. */
	link_on_hub?: number | null;
	/** Public URL of the published smart-link page; stable across re-publishes */
	link_url?: string | null;
	link_published_at?: string | null;
	/** The fan participation kit at /<slug>/kit on the hub (see services/fanKit.ts) */
	kit_on_hub?: number;
	kit_url?: string | null;
	kit_published_at?: string | null;
	/** Smart-link appearance (JSON {layout?, accent?, font?}); null = classic stack, cover-derived colors */
	link_theme?: string | { layout?: "card" | "stack" | null; accent?: string | null; font?: string | null } | null;
	/** Spotify for Artists editorial pitch (≤500 chars, pasted into S4A manually) */
	s4a_pitch?: string | null;
	s4a_pitched_at?: string | null;
	/** The other platforms' pitch texts, a JSON map {platform id: text} (v155, round 359) */
	platform_pitches?: string | null;
	/** AI song analysis (JSON string in the DB; may arrive parsed in API responses) */
	song_analysis?: SongAnalysis | string | null;
	song_analyzed_at?: string | null;
	/** Detected audio sections (SectionAnalysis JSON string in the DB) */
	audio_sections?: SectionAnalysis | string | null;
	sections_analyzed_at?: string | null;
	/** Measured beat grid (BeatGridAnalysis JSON string in the DB); null = not analyzed yet */
	beat_grid?: BeatGridAnalysis | string | null;
	/** Tap-synced lyric line timestamps (SyncedLyrics JSON string in the DB) */
	lyrics_synced?: SyncedLyrics | string | null;
	/** Direct URL of the owner's own music video (source for video-background shorts) */
	video_url?: string | null;
	/** Name of an UPLOADED video file kept on the release (video_url is null then; v94). The
	 * studio card's only handle on a local source - there is no URL to show. */
	video_src_name?: string | null;
	/** Audio alignment of the music video against the uploaded master (VideoSync JSON) */
	video_sync?: VideoSync | string | null;
	/** How this release's reels are typeset (ReelTheme JSON; null = each look's own defaults) */
	reel_theme?: ReelTheme | string | null;
	/** AI parental-advisory classification from lyrics */
	explicit_status?: "clean" | "explicit" | null;
	explicit_reason?: string | null;
	/** Shared-DB tenancy (v98). Selfhost has exactly one workspace, so this is always 1 there.
	 *  ⚠ Where a caller has selected it, it is the strongest thing to scope a SIBLING read by:
	 *  `emailComposer` picks the pitch template off `release.workspace_id` rather than off ambient
	 *  scope, so the campaign daemon cannot compose in another tenant's words (round 138). */
	workspace_id?: number;
	/** Runway rung keys the owner waved off for this release (JSON ["t14",…]; v69) */
	runway_ignored?: string | null;
	/** 🤖 Autopilot switch + working memory (AutopilotConfig JSON, v100); NULL = disarmed */
	autopilot?: string | null;
	/** List endpoints omit the base64 cover_image and set this instead */
	has_cover?: boolean;
	/**
	 * Which catalog this release was picked from, and its id there (v124). ⚠ A PAIR, never an id
	 * alone - see the migration: one neutral id column collides across vendors. Read by the audio
	 * preview lane and, since 2026-08-19, by the cover revert.
	 */
	catalog_vendor?: string | null;
	catalog_album_id?: string | null;
	/** Story angles on this release (list endpoint only) */
	angles_count?: number;
	/** The master the workspace header plays (§AL round 4) - list rows only, MP3 first; null when there is none */
	master_audio_id?: number | null;
	/** Uploaded MP3/WAV files linked to this release (detail endpoint only) */
	audio_files?: AudioFile[];
}

export interface Artist {
	id: number;
	/** Shared-DB tenancy (v96). Selfhost has exactly one workspace, so this is always 1 there */
	workspace_id?: number;
	name: string;
	artist_origin: string | null;
	/** Where the artist is currently based (may differ from artist_origin) */
	current_location: string | null;
	artist_bio: string | null;
	artist_bio_short: string | null;
	artist_press_highlights: string | null;
	social_links: SocialLinks | string | null;
	pronouns: string | null;
	avatar_image: string | null;
	created_at: string;
	/** Netlify site backing the published link-in-bio page (created once, re-deployed in place) */
	bio_site_id?: string | null;
	/** Is the link-in-bio page on the hub? See `Release.epk_on_hub` on why both halves are needed. */
	bio_on_hub?: number | null;
	/** Public URL of the published link-in-bio page; stable across re-publishes */
	bio_url?: string | null;
	bio_published_at?: string | null;
	/** Custom link-in-bio rows (JSON [{label, url}] in the DB) */
	bio_links?: string | { label: string; url: string }[] | null;
	/** Link-in-bio curation (JSON {layout, hidden: [keys], order: [keys]} in the DB) */
	bio_theme?:
		| string
		| { layout?: "stack" | "horizontal" | null; hidden?: string[]; order?: string[]; font?: string | null; preview?: "hook" | "off" | null }
		| null;
	release_count?: number;
	/** List endpoints omit the base64 avatar_image and set this instead */
	has_avatar?: boolean;
}

export interface Outreach {
	id: number;
	blog_id: number;
	release_id: number;
	template_id: number | null;
	status: "draft" | "queued" | "approved" | "sent" | "failed";
	email_subject: string | null;
	email_body: string | null;
	personalization_notes: string | null;
	is_follow_up: number;
	original_outreach_id: number | null;
	created_at: string;
	approved_at: string | null;
	sent_at: string | null;
	follow_up_after_days: number;
	gmail_message_id: string | null;
	gmail_thread_id: string | null;
	/** RFC 2822 Message-ID of the sent message (used to thread follow-ups) */
	rfc_message_id?: string | null;
	/** Set when a reply from the blog was detected in the Gmail thread */
	replied_at?: string | null;
	/** AI triage of the blog's reply */
	reply_classification?: ReplyClassification | null;
	reply_summary?: string | null;
	reply_suggested_response?: string | null;
	/** A/B subject test variant ('A' | 'B'), null when not part of a test */
	subject_variant?: string | null;
	/** Genre-matched editor address chosen at compose time (NULL = the outlet's default email) */
	recipient_email?: string | null;
	/** The named editor this pitch was addressed to, when one was matched */
	editor_id?: number | null;
	error_message: string | null;
}

export type ReplyClassification = "accepted" | "interested" | "rejected" | "payment_requested" | "auto_reply" | "bounce" | "other";

export interface ReplyTriage {
	classification: ReplyClassification;
	summary: string;
	suggested_response: string | null;
}

/** An editor/host/staff member at an outlet - the person coverage actually comes from */
export interface Editor {
	id: number;
	/** NULL for freelancers not tied to one outlet */
	blog_id: number | null;
	name: string;
	role: string | null;
	/** ⚠ `null` on the wire in saas unless this workspace created the outlet (§AB round 2) - a person's
	 *  address at a publication is as scraped as the publication's. Ask `emailable`. */
	email: string | null;
	/** Whether the server holds an address for this person (§AB round 2; both editions) */
	emailable?: boolean;
	email_status?: string | null;
	email_checked_at?: string | null;
	instagram: string | null;
	twitter: string | null;
	/** Author/staff page */
	url: string | null;
	/** Comma-separated beats, matched against release genres at compose time */
	genres: string | null;
	notes: string | null;
	source: "manual" | "staff_page" | "byline" | "search";
	verified: number;
	disabled: number;
	created_at: string;
}

export interface OutreachWithDetails extends Outreach {
	blog_name: string;
	/** ⚠ `null` on the wire in saas, for every outlet (§AB round 2 - no ownership carve-out on a pitch
	 *  row; the card's question is *who did this go to*, which `blog_name` answers better). */
	blog_email: string | null;
	/**
	 * The pitch went to something other than the outlet's default inbox - a genre-matched desk or a
	 * named editor.
	 *
	 * ⚠ **Derived on the SERVER, before the addresses are masked** (§AB round 2). The pitch card used
	 * to compare `recipient_email` with `blog_email` in the browser; with both masked that comparison
	 * silently answers "same", so the badge would have disappeared for a reason that has nothing to do
	 * with the pitch.
	 */
	recipient_alternate?: boolean;
	blog_url: string;
	/** The outlet has asked for payment (in a reply or scraped signals) - flag, not a block */
	pay_to_play?: number | null;
	/** The outlet has offered GUARANTEED streams/placement - a Spotify-ToS red flag (v78) */
	stream_guarantee?: number | null;
	submission_guidelines: string | null;
	subject_format: string | null;
	release_title: string;
	artist_name: string;
	release_type: string;
	/** Cached AI fit score (0-100) + reason for this blog/release pair, if scored */
	fit_score?: number | null;
	fit_reason?: string | null;
	/** AI fit + learned track-record adjustment, clamped 0–100 (null when never scored) */
	effective_fit_score?: number | null;
	/** Learned per-outlet adjustment (±15) and what it is made of */
	track_adjustment?: number | null;
	track_evidence?: TrackEvidenceCounts | null;
	/** Distinct tracking events (only included on list endpoints) */
	open_count?: number;
	click_count?: number;
	/** Press-kit views attributed to this recipient's tokened link (list endpoints only) */
	epk_view_count?: number;
	/** Name of the editor the pitch was addressed to (joined from editors) */
	editor_name?: string | null;
}

export interface EmailTemplate {
	id: number;
	name: string;
	subject: string;
	body: string;
	/** When set, compose auto-picks this template for targets of this site type */
	site_type?: SiteType | null;
	subject_locales: string | null;
	body_locales: string | null;
	after_release_subject: string | null;
	after_release_body: string | null;
	after_release_subject_locales: string | null;
	after_release_body_locales: string | null;
	is_follow_up: number;
	created_at: string;
}

export interface Campaign {
	id: number;
	release_id: number;
	enabled: number;
	template_id: number | null;
	/** Compose initial pitches when the release is this many days away (or closer) */
	pitch_days_before: number;
	auto_followups: number;
	/** Keep composing for not-yet-contacted blogs after the release is out */
	out_now_enabled: number;
	/** Don't compose pitches to outlets scoring below this fit (0–100) */
	min_fit_score: number;
	/** Targets shown against the live funnel; null = no goal */
	goal_opens?: number | null;
	goal_replies?: number | null;
	goal_coverage?: number | null;
	last_run_at: string | null;
	last_run_status?: "done" | "error" | null;
	last_run_composed?: number | null;
	last_run_skipped?: number | null;
	last_run_finished_at?: string | null;
	/** ISO time of the next automatic scheduler sweep; null while paused */
	next_check_at?: string | null;
	created_at: string;
	// Joined release info on list endpoints
	release_title?: string;
	artist_name?: string;
	release_date?: string | null;
	release_type?: string;
	/** Whether the release has cover art (the campaign card shows it) */
	has_cover?: boolean;
	queued_count?: number;
	approved_count?: number;
	sent_count?: number;
	/** The workspace's cap on unsent pitches per release (list endpoint only) - the card says "Queue full" against it */
	queue_limit?: number;
	/** Live in-process run state (null when idle / never run this process) */
	run?: CampaignRunState | null;
	/** Response funnel for the campaign's release (list endpoint only) */
	funnel?: {
		sent: number;
		opened: number;
		replied: number;
		covered: number;
		rejected: number;
		ab: { variant: string; sent: number; opened: number; replied: number }[];
	};
	/** Release-date-anchored pitch waves (list endpoint only; empty = single-window pitching) */
	waves?: CampaignWave[];
	days_until_release?: number | null;
}

/** One pitch wave of a campaign (see src/services/waves.ts) */
export interface CampaignWave {
	id: number;
	campaign_id: number;
	label: string;
	/** Days relative to the release date; -45 = 45 days before */
	offset_days: number;
	/** JSON array of site types; null = all types */
	site_types: string | null;
	min_fit_score: number | null;
	/** Story angle (release_angles.id) this wave's intros lead with; null = none */
	angle_id?: number | null;
	/** The angle's label, joined for display */
	angle_label?: string | null;
	status: "planned" | "dispatched" | "skipped";
	dispatched_at: string | null;
	composed_count: number | null;
}

/** Live progress of a background campaign run (mirrors CampaignRunState on the server) */
export interface CampaignRunState {
	campaignId: number;
	status: "running" | "done" | "error";
	total: number;
	processed: number;
	composed: number;
	skippedPoorFit: number;
	followUps: number;
	/** Targets left for a later run because the release's queue was at its cap (Settings, "Pitches waiting at a time") */
	heldBack: number;
	startedAt: string;
	finishedAt?: string;
	error?: string;
}

export interface ScrapeJob {
	id: number;
	status: "pending" | "running" | "completed" | "failed";
	query: string | null;
	results_count: number;
	started_at: string | null;
	completed_at: string | null;
	error_message: string | null;
	created_at: string;
}

export interface BlogAnalysis {
	isMusicBlog: boolean;
	acceptsSubmissions: boolean;
	isFree: boolean;
	acceptsSingles: boolean;
	blogName: string;
	email: string | null;
	/** Genre-specific editor emails found on the page (empty when the site has one address) */
	genreEmails?: GenreEmailEntry[];
	description: string;
	genreFocus: string;
	submissionGuidelines: string;
	subjectFormat: string;
	localOnly: boolean;
	acceptedRegions: string;
	language: string;
	siteType?: SiteType;
}

export interface SearchResult {
	title: string;
	url: string;
	snippet: string;
}

export interface DashboardStats {
	totalBlogs: number;
	verifiedBlogs: number;
	totalReleases: number;
	drafts: number;
	queued: number;
	approved: number;
	sent: number;
	failed: number;
}

export interface TemplateVariables {
	outlet_name: string;
	/** The editor/host the pitch is addressed to; empty when none matched */
	editor_name: string;
	editor_first_name: string;
	/** Milestone text for nurture notes ("Share news with champions"); empty otherwise */
	milestone: string;
	outlet_url: string;
	artist_name: string;
	release_title: string;
	release_type: string;
	genre: string;
	primary_genre: string;
	moods: string;
	primary_mood: string;
	instruments: string;
	primary_instrument: string;
	promo_blurb_long: string;
	promo_blurb_short: string;
	about: string;
	artist_bio: string;
	artist_bio_short: string;
	artist_press_highlights: string;
	press_kit_url: string;
	epk_url: string;
	/** The published smart link; empty until published */
	smart_link_url: string;
	/** The smart link tagged for THIS outlet (?src=creator-<id>-<slug>) - its traffic reports under their name */
	tagged_link_url: string;
	/** The published fan kit (/<slug>/kit); empty until published */
	fan_kit_url: string;
	streaming_links: string;
	personalized_intro: string;
	submission_guidelines: string;
	subject_format: string;
	artist_origin: string;
	current_location: string;
	ean_upc: string;
	isrc: string;
	release_date: string;
	recording_year: string;
	lyrics: string;
	duration: string;
	bpm: string;
	album_title: string;
	producer: string;
	recording_equipment: string;
	recording_studio: string;
	composed_by: string;
	recorded_by: string;
	mixed_by: string;
	mastered_by: string;
	label: string;
	collaborators: string;
	response_email: string;
	site_type: string;
	phone_number: string;
	spotify: string;
	apple_music: string;
	soundcloud: string;
	deezer: string;
	youtube: string;
	instagram: string;
	tiktok: string;
	facebook: string;
	youtube_social: string;
	spotify_social: string;
	apple_music_social: string;
	deezer_social: string;
	song_genre: string;
	song_subgenres: string;
	song_moods: string;
	song_instruments: string;
	song_vocals: string;
	song_tempo: string;
	song_energy: string;
	song_sound: string;
	explicit_status: string;
	related_links: string;
	[key: string]: string;
}

export interface GroupedOptions {
	group: string;
	items: string[];
}

/**
 * Release pre-flight (round 56): the readiness of a release to be pitched, DERIVED from the DB
 * (never a stored counter - same rule as the onboarding shell). A "blocker" is something that
 * makes the pitch land a stranger somewhere they cannot hear the song - the one failure the whole
 * app exists to prevent; a "warning" is soft; "info" is a heads-up that is fine right now (no
 * streaming links before release day). The gate SURFACES this before the first wave; it never
 * hard-stops - the owner can always pitch anyway.
 */
export type PreflightSeverity = "blocker" | "warning" | "info";

/**
 * WHICH QUESTION a check answers, and therefore which surface shows it and whose score it moves
 * (2026-08-24, when the gate grew a sibling for artists and a percentage for both).
 *
 * ⚠ **THE finding this field exists for: a blocker list and a completeness score are two different
 * questions asked of the SAME facts, and collapsing them breaks whichever one you did not have in
 * mind.** Compose asks *will this pitch hit a wall* - a small, urgent, binary list, and burying it
 * under "the credits are empty" is how a page that says *3 things to fix before you pitch* stops
 * being read. The meter asks *how good is this pitch* - and a list of only the walls can never
 * climb past the walls, so a release with a master, a cover and two published pages sits at 100%
 * with no story, no genre and no credits. One registry, two lenses, and this is the lens.
 *
 * - `pitch` - a wall a stranger hits if this pitch goes out. On both surfaces; scored.
 * - `profile` - information that makes the pitch better rather than possible. Meter only; scored.
 * - `workspace` - ⚠ **a fact about the INSTALL, not about this release or this artist** (today:
 *   whether the sending domain publishes SPF and DMARC). Shown on the gate, where the spend is
 *   about to happen, and **never scored** - see `readinessScore`, which refuses it by construction.
 *   Counting it would cap EVERY release in the workspace below 100 for a reason that has nothing to
 *   do with any of them, and a ceiling nobody can reach is how a score stops being worth climbing.
 */
export type ReadinessScope = "pitch" | "profile" | "workspace";

/**
 * A readiness check speaks in i18n KEYS, not prose (round 70). This is server-derived text
 * rendered by app chrome, which is the one shape neither existing seam covered: `t()` lives in
 * the browser and `epkStrings` belongs to the published pages, so a sentence built on the server
 * for the Compose panel had no way to be anything but English. Naming the key and handing over
 * the parameters lets the ISLAND render it through `t()` like every other string on the page -
 * and it is what let the streaming line stop hand-pluralizing ("link" + "s"), which no
 * translator could have used.
 *
 * ⚠ **`F` is the FIX destination, and it is generic for round 162's reason rather than for
 * tidiness.** Every subject this shape serves navigates somewhere different - a release has tabs
 * and editor accordions, an artist has its own set - and a shared `{ tab: string }` is exactly the
 * hole that round put `tab: "audio"` into, a subpage this app has never had, which the router
 * resolved by silently ignoring it. A generic keeps each subject's destinations a CLOSED union
 * while the rows, the scoring and the renderer stay one implementation.
 */
export interface ReadinessCheck<F> {
	key: string;
	/** i18n key of the check's state-specific short name ("Smart link is live" / "Smart link is not live") */
	labelKey: string;
	ok: boolean;
	/**
	 * How much a failure of THIS check matters - only failing blockers hold back "ready".
	 *
	 * ⚠ **It is also the check's WEIGHT in the score, deliberately, so there is no second number to
	 * keep in step.** A per-check weight field would be one more thing every new check has to decide
	 * and the one thing nothing would ever check - and it would let a check claim to be a blocker and
	 * score like a footnote. Severity is a judgement the author already has to make; `SEVERITY_WEIGHT`
	 * in `shared/readiness.ts` is the whole of the mapping.
	 */
	severity: PreflightSeverity;
	/** Which question this check answers - see `ReadinessScope`. */
	scope: ReadinessScope;
	/** i18n key of the one honest sentence for the state this check is actually in */
	detailKey: string;
	/** Named parameters the detail interpolates (today: the streaming-link count) */
	detailParams?: Record<string, string | number>;
	/**
	 * Where to go to fix it: `tab` is a release-workspace sub-tab key ("epk", "link", "sections"),
	 * "" means the release detail, null means there is nowhere to send them (a passing check, or a
	 * Settings-level prerequisite named in the detail).
	 *
	 * ⚠ **`dialog` opens the EDIT DIALOG, on the accordion that owns the field** (2026-08-11, owner:
	 * *"when I click Upload the master I am taken to the Sections & Lyrics page - instead I should
	 * have been taken to the Edit Release dialog, and even better if it auto-expanded the Audio,
	 * lyrics and analysis accordion, so I could upload the track right then and there"*). A fix
	 * button that lands the reader on the right PAGE and leaves them to find the control is a
	 * signpost; one that opens the control is a fix. `""` means the dialog with nothing expanded (the
	 * cover lives above the accordions), and a named section is one of the editor's own keys.
	 *
	 * ⚠ **Both destinations were bare `string`s until §I.5 round 3, which is round 162's bug with the
	 * fuse still in.** That round put `tab: "audio"` - a subpage this app has never had - into the
	 * identically-typed field on `AttentionRow`, and the owner clicking "the master file is missing"
	 * was shown the press kit by a router that resolves an unknown sub-path by ignoring it. Every
	 * gate stayed green then and would have stayed green here. `""` stays legal in both, because in
	 * both it MEANS something (the release detail; the dialog with nothing expanded).
	 */
	fix: F | null;
}

/** Where a failing RELEASE check sends the reader. See `ReadinessCheck.fix` for the two destinations. */
export interface ReleaseFix {
	tab: ReleaseTab | "" | null;
	dialog?: ReleaseEditorSection | "";
	labelKey: string;
}

/**
 * Where a failing ARTIST check sends the reader.
 *
 * ⚠ **`dialog` names an ARTIST editor accordion and `tab` an artist sub-tab** - the same two
 * destinations as the release, over that page's own vocabulary, which is the entire reason
 * `ReadinessCheck` is generic. Both unions are closed, so a fix pointing at a section the modal does
 * not have fails the typecheck rather than the reader.
 */
export interface ArtistFix {
	tab: ArtistTab | "" | null;
	dialog?: ArtistEditorSection | "";
	labelKey: string;
}

/** The release gate's rows - the historical name, kept because six modules and a route say it. */
export type PreflightCheck = ReadinessCheck<ReleaseFix>;
export type ArtistCheck = ReadinessCheck<ArtistFix>;

/**
 * How complete a subject is, as one number the owner can watch climb (owner ask, 2026-08-24:
 * *"some form of progress bar that indicates how complete the information is yet, with a score in
 * terms of how ready the artist/release is for outreach ... it gamifies the platform"*).
 *
 * ⚠ **`possible` is computed from the checks that are actually PRESENT, never from a constant**, and
 * that is what makes 100 reachable. `preflight.ts` already had the habit for its own reason - the
 * signup-form check is omitted rather than failed once the song is out, because it is a question
 * about a form that no longer exists - and a score is what turns that habit into a rule: **a check
 * the subject cannot satisfy must be left OUT of the denominator, not counted as a failure.** A
 * ceiling of 80% that no amount of work can move reads as the app being broken, and it is the exact
 * opposite of the thing the owner asked for.
 *
 * ⚠ **Never stored and never compared across time.** Round 185's finding applies to this number more
 * than to anything else in the app: the day a round adds two checks, every artist's score drops with
 * nothing having changed about their work, so a chart of it would be a chart of our release history
 * wearing their name. It is derived on every read, like every other row in this family.
 */
export interface ReadinessScore {
	/** 0-100, rounded. What the bar fills to and the number beside it. */
	percent: number;
	/** Weight earned by passing checks, and the weight available from the checks that apply today. */
	earned: number;
	possible: number;
	/** How many scored checks are still failing - the "N left" the meter counts down. */
	remaining: number;
}

export interface PreflightResult {
	releaseId: number;
	title: string;
	/** The release date has arrived (local calendar) - flips streaming-links from a heads-up to a blocker */
	released: boolean;
	checks: PreflightCheck[];
	/** Count of FAILING checks at each severity */
	blockers: number;
	warnings: number;
	/** No failing blocker remains - safe to pitch without a stranger hitting a wall */
	ready: boolean;
	/** A non-follow-up pitch already exists for this release - the wave has started, so the gate reads as a heads-up */
	pitched: boolean;
	/** How complete this release is, over its `pitch` and `profile` checks. See `ReadinessScore`. */
	score: ReadinessScore;
}

/**
 * The ARTIST half of the same lane (2026-08-24). Same rows, same scoring, same renderer; a
 * different subject and a different set of destinations.
 *
 * ⚠ **There is no `ready` and no `blockers` here, and the absence is the design.** A release gate
 * guards an ACT - the wave about to be sent - so "may I proceed" is a real question with a real
 * answer. Nothing on the artist page is about to spend anything, so a `ready: false` would be a
 * verdict on a person's profile with no action attached to it: a nag, which is the one thing this
 * family is not allowed to become. Severity still ranks the rows and still weights the score; what
 * it does not do here is refuse anything.
 */
export interface ArtistReadiness {
	artistId: number;
	name: string;
	checks: ArtistCheck[];
	score: ReadinessScore;
}

/**
 * 🤖 Release autopilot - the card's payload (round 121).
 *
 * The last surface in the app where the server still WROTE the owner's sentences, and the largest
 * one: eight refusal reasons, seven per-rung plan notes, the stall errors and the pause. Same seam
 * and same reason as `PreflightCheck` above (round 70) - the autopilot decides which sentence is
 * TRUE, the Runway island says it through `t()`. It arrived three rounds later than the rest
 * because two of these sentences were PERSISTED inside the release's `autopilot` JSON column, so
 * converting them was a stored-state question as well as a seam one. The two answers differ, and
 * which one applies is worth knowing before adding a third:
 *
 * - **The pause is DERIVED, not stored.** "Instagram is no longer connected" is a fact about the
 *   world the reader can check for free, so storing it only bought a copy that goes stale between
 *   ticks (reconnect, and the card kept saying paused for up to five minutes). It is now computed
 *   by the same `autopilotPause()` predicate the tick stands down on, so the card and the daemon
 *   cannot disagree - the round-120 `notifyDestination()` rule.
 * - **A rung's stall error IS stored**, because it is genuine working memory (what failed, on which
 *   attempt) and nothing outside the tick knows it. So it stores the KEY, not the sentence.
 *
 * Both halves are `AutopilotText`, and so is everything else the card reads, so a caller never has
 * to ask which of the two a given field is.
 */
export type AutopilotText = KeyText;

/**
 * A sentence the SERVER decided and the reader's side says: the i18n key, plus the values it
 * interpolates. The round-70 seam's payload, in one shape.
 *
 * It was `AutopilotText` alone until round 123 gave the render refusals the same treatment and found
 * itself about to declare a second identical interface. There are now several fields of this shape
 * across the app (`AutopilotStatus.blockers`, a rung's stall, a render's refusal), and a reader that
 * has to ask "which of the identical types is this one" gets nothing for the asking. `PreflightCheck`
 * and `TrustSignal` keep their own field names (`labelKey`/`detailKey`, `code`) because those payloads
 * carry SEVERAL sentences apiece and renaming a shipped wire field buys nothing.
 */
export interface KeyText {
	/** i18n key of the one honest sentence for this state ("runway.ap.block.audio") */
	key: string;
	/**
	 * Named parameters the sentence interpolates. `detail` is the escape hatch and is deliberately
	 * narrow: a diagnostic composed somewhere ELSE (ffmpeg's render error, Instagram's publish
	 * refusal) rides verbatim inside a translated frame rather than being faked into a key we do
	 * not own. `date` arrives as ISO `YYYY-MM-DD`, and the reader formats it - only that side knows
	 * the locale (`frontend/utils.ts`'s `formatDate`, round 123's `Intl.DateTimeFormat`).
	 */
	params?: Record<string, string | number>;
	/**
	 * Named parameters whose value is itself a decided phrase, resolved by the reader before the
	 * frame interpolates it (round 124). The house rule is "never concatenate a sentence out of
	 * fragments", and this does not break it: **a part may only be a NOUN PHRASE that fills a slot
	 * in a fixed frame** - a thing, a place - never a clause and never a whole sentence.
	 *
	 * It exists because the mentions lane's sentences NAME things the app also has to translate:
	 * "commented {where} with a link to {link}", where `where` is one of six places a comment can
	 * sit and `link` one of eleven things of the owner's a stranger can link to. Enumerating that
	 * as whole sentences is 3 × 6 × 11 keys, which is not a dictionary anyone maintains - and the
	 * alternative the app had been living with was worse: compose the phrase in English on the
	 * server and pass it as a plain param, which is how a translated frame ends up with English
	 * inside it. A translator can inflect a noun phrase into a prepared slot; they cannot reorder a
	 * sentence they were never shown.
	 */
	parts?: Record<string, KeyText>;
}

/**
 * A sentence read back out of a COLUMN: `{ key }` when the row was written by a version that stores
 * the decision, `{ text }` when it predates that seam and the column holds the English the app
 * composed at the time (round 124, generalizing round 121's tolerate-old-rows rule and round 122's
 * `ChannelLabel`).
 *
 * The two arms are disjoint by construction - `KeyText` has no `text`, the raw arm has no `key` - so
 * `"key" in stored` is a real discriminant here, unlike the trap round 123 paid for. Old rows are
 * tolerated at parse time and never migrated: they say what was true when they were written, and the
 * next sweep that touches the row upgrades it to a key by itself.
 */
export type StoredText = KeyText | { text: string };

/** How a decided sentence goes INTO a TEXT column - as its decision, so no English is ever stored */
export function encodeStoredText(text: KeyText): string {
	return JSON.stringify(text);
}

/**
 * …and how it comes back out. Anything that is not our JSON is a sentence from before the column
 * joined the seam, and rides on verbatim: told apart BY SHAPE, the round-123 rule that also lets a
 * foreign diagnostic (ffmpeg's, a platform's) share a column with our own keys.
 */
export function parseStoredText(stored: string | null | undefined): StoredText | null {
	if (stored == null) return null;
	const trimmed = stored.trim();
	if (trimmed.length === 0) return null;
	if (!trimmed.startsWith("{")) return { text: stored };
	try {
		const parsed: unknown = JSON.parse(trimmed);
		if (parsed != null && typeof parsed === "object" && typeof (parsed as KeyText).key === "string") return parsed as KeyText;
	} catch {
		// Not ours - an English sentence that merely happens to start with a brace
	}
	return { text: stored };
}

/**
 * A publish warning together with WHICH published page raised it (round 150).
 *
 * Every renderer's own `warnings` is a flat `KeyText[]` - the reader of `/link/publish` already
 * knows it pressed Publish on the smart link, so naming the surface there would be chrome. The one
 * list that spans several pages is the whole-hub deploy's aggregate, and until this round it kept
 * the surface by writing `` `Bio: ${warning}` `` - the house rule against composing a sentence out
 * of fragments, broken in the one place no gate looks.
 *
 * ⚠ **`parts` cannot fix that**, and the reason is worth knowing before the next caller reaches for
 * it: a part may only be a NOUN PHRASE filling a prepared slot, never a clause and never a whole
 * sentence, because a translator can inflect a phrase and cannot reorder a sentence they were never
 * shown. What was wrong here was the flattening itself - **a label and the sentence it labels are
 * two pieces of UI, not one sentence** - so the fix is structural rather than a cleverer frame.
 */
export interface ScopedWarning {
	/** The page it is about, as a noun phrase: "Smart link (midnight-static)" */
	surface: KeyText;
	/** The warning itself, exactly as that page's own renderer said it */
	text: KeyText;
}

/**
 * The release workspace's subnav - the CLOSED list of tabs a deep link may name (round 169).
 *
 * ⚠ It lives here because the SERVER names one and the FRONTEND owns the set, and until this round
 * the field between them was a bare `string`. Round 162's silent-page row put `tab: "audio"` in it,
 * a subpage this app has never had (uploading a master is done in the release EDITOR modal, not on a
 * tab of its own) - and `releases.ts` resolves an unknown tab by ignoring it, so the owner clicking
 * "the master file is missing from disk" was navigated to `#releases/<id>/audio` and shown the press
 * kit. **Every gate stayed green**: `tsc` was satisfied by `string`, the backend test asserted `goto`
 * and never the tab, the i18n audit sees no literal, and the strip's own UI tests clicked the two
 * rows whose tab happened to be real.
 *
 * This is round 150's rule where it applies most cleanly - *prefer the TYPE where the class has a
 * shape one can express* - and the shape here is a list of ten strings. `pages/releases.ts` imports
 * this rather than declaring its own, so the union has ONE home; the render sites pass the value
 * straight through instead of casting it, because **a cast at the boundary launders exactly the
 * mistake the union exists to catch.**
 *
 * ⚠ **It is the ARRAY that is declared and the union that is DERIVED** (round 179, finishing what
 * round 178 did for `SETTINGS_TABS` - this file was the one round 178 filed and left). Round 169
 * shared the union and left `pages/releases.ts` holding its own hand-written list of the same ten
 * strings in the same order, which is only half-checked: an element type catches a list entry that
 * is not in the union and says NOTHING about a union member the list omits - and since the router
 * matches a sub-path against the LIST, an omission is round 169's bug exactly, restored. The tab
 * would typecheck everywhere, the deep link would be dropped on the floor, and the owner would land
 * on the press kit again. One declaration cannot disagree with itself.
 */
export const RELEASE_TABS = ["epk", "link", "campaign", "insights", "pitch", "angles", "sections", "clips", "runway", "kit", "ads"] as const;

/**
 * Tab names that used to be something else, and the name they mean now.
 *
 * ⚠ **A tab name is half of an ADDRESS, so renaming one breaks links that already exist** - a
 * bookmark, a support answer that named the page, the run-of-show for a screencast. `shorts` became
 * `clips` on 2026-08-20 when the page did, and the old spelling still has to arrive somewhere rather
 * than falling through to the default tab, which is the failure that looks like the app forgetting
 * where you were. **The row stays forever; it costs one line and the alternative is a dead link.**
 */
export const RELEASE_TAB_ALIASES: Record<string, ReleaseTab> = { shorts: "clips" };
export type ReleaseTab = (typeof RELEASE_TABS)[number];

/**
 * A release's eleven pages in FIVE groups (§AL round 3): Pages, Clips, Pitch, Insights, Ads - the
 * spec's *"a release's 11 pages become 5 tabs"*. The group is DERIVED from the tab and never stored or
 * routed: `/releases/<id>/angles` is still the address, and it opens the Pitch group with Angles
 * chosen, so every deep link that names a tab (attention rows, preflight fixes, a support answer) is
 * untouched. `tests/releaseTabs.test.ts` holds the groups to a partition of `RELEASE_TABS`: a tab in no
 * group would be a page the workspace can reach and no button can.
 */
export const RELEASE_TAB_GROUPS = [
	{ group: "pages", tabs: ["epk", "link", "kit"] },
	{ group: "clips", tabs: ["sections", "clips", "runway"] },
	{ group: "pitch", tabs: ["campaign", "pitch", "angles"] },
	{ group: "insights", tabs: ["insights"] },
	{ group: "ads", tabs: ["ads"] }
] as const satisfies readonly { group: string; tabs: readonly ReleaseTab[] }[];
export type ReleaseTabGroup = (typeof RELEASE_TAB_GROUPS)[number]["group"];

/** The group a tab is shown under. */
export function releaseTabGroup(tab: ReleaseTab): (typeof RELEASE_TAB_GROUPS)[number] {
	return RELEASE_TAB_GROUPS.find(entry => (entry.tabs as readonly ReleaseTab[]).includes(tab)) ?? RELEASE_TAB_GROUPS[0];
}

/**
 * The Settings page's subpages, in the order the tab strip shows them - the same story as
 * `ReleaseTab`, found in round 178 by asking round 169's question of the sibling field.
 *
 * ⚠ **A `goto: "settings"` row had no way to name a tab at all, so all five of them landed on
 * Templates** - where none of the five things can be fixed. The Gmail connection, the tracking beacon
 * and the custom domain are on Connections; the plan and the AI budget are on Account. Round 169's
 * finding was that a strip row deep-linking to the wrong place is invisible to every gate; this is
 * the same row family arriving at a real tab that is simply not the one that helps, which is milder
 * only because nothing looks broken when you get there.
 *
 * ⚠ **It is the ARRAY that is shared and the union that is derived, which is one step past round
 * 169.** A hand-written union beside a hand-written runtime list is only half-checked: `satisfies`
 * (or an element type) catches a list entry that is not in the union, and says nothing about a union
 * member the list omits - and since the router matches a sub-path against the LIST, that omission is
 * round 169's bug exactly, restored. One declaration cannot disagree with itself.
 */
export const SETTINGS_TABS = ["templates", "sending", "localization", "ai", "connections", "appearance", "accessibility", "account", "data"] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

/**
 * The release EDITOR's five collapsible sections - the third member of the family above, and the one
 * that lets the server address a place that is not a page (§I.5 round 3).
 *
 * ⚠ **Round 255 filed the sync lane's readiness prompt as blocked on exactly this**, in these words:
 * *`AttentionRow` can address a page, a release tab and a settings tab but not a modal.* That was
 * true of the FIELD and false of the APP. `router.ts`'s `syncDialog` has given a dialog an address
 * since 2026-08-11, the releases page has parsed `edit-<id>-credits` since the same day, and
 * `preflight.ts` has been producing exactly such a link from the compose screen ever since - one
 * module over, through a `fix.dialog` field of its own. **A blocker recorded against one payload is
 * a fact about that payload, and it expires silently when a sibling grows the mechanism.** The same
 * shape as rounds 227, 232, 233 and 250, whose entries all say a written refusal reads like a
 * decision forever after; this one only had to be asked of the app rather than of the type.
 *
 * ⚠ **It is the ARRAY that is declared and the union DERIVED**, for rounds 178/179's reason - and
 * here that reason has a second edge, because `pages/releases.ts` matches an arriving address
 * against these names with a REGEX. A hand-written alternation beside a hand-written union is the
 * half-checked pair those rounds closed twice: the regex is built from this list now, so a section
 * added to the editor cannot be one the address bar refuses to carry.
 *
 * ⚠ **`preflight.ts`'s `fix.dialog` was a bare `string`** and is this type now. That is round 162's
 * bug exactly - `tab: "audio"`, a subpage this app has never had, satisfied `string` and navigated
 * the owner to the press kit - and the field had been sitting one refactor away from it since the
 * day it shipped.
 */
export const RELEASE_EDITOR_SECTIONS = ["story", "audio", "artwork", "credits", "links"] as const;
export type ReleaseEditorSection = (typeof RELEASE_EDITOR_SECTIONS)[number];

/**
 * The ARTIST editor's four collapsible sections - the same list, for the same reason.
 *
 * ⚠ **It moved out of `pages/artists.ts` on 2026-08-24 because the SERVER now names one of these.**
 * `artistReadiness.ts` points a fix button at the accordion that owns the missing field, and a
 * server naming a frontend constant it cannot see is exactly the gap round 162's `tab: "audio"` fell
 * through. The page still owns the accordions; `shared/` owns what they are CALLED, so the two
 * cannot disagree. (`where` is origin/location/pronouns - see the modal's own note on why that
 * cluster stays one section.)
 */
export const ARTIST_EDITOR_SECTIONS = ["bio", "where", "photos", "socials"] as const;
export type ArtistEditorSection = (typeof ARTIST_EDITOR_SECTIONS)[number];

/**
 * Narrow a router sub-path to a tab that really exists.
 *
 * ⚠ These are the last two casts in the tab story, and a cast is what let round 169's bug through a
 * typecheck in the first place. Both routers had the shape `if (TABS.includes(sub)) this.tab = sub as
 * Tab` - correct, but only because the guard on the left happens to match the assertion on the
 * right, which is precisely the thing a reader cannot check at a glance and a refactor can break
 * silently. A predicate says it once, and the compiler holds the two halves together.
 */
export function isReleaseTab(value: string | undefined): value is ReleaseTab {
	return value != null && (RELEASE_TABS as readonly string[]).includes(value);
}

/** The tab this path segment means, following a rename - null when it names no tab at all */
export function releaseTabOf(value: string | undefined): ReleaseTab | null {
	if (value == null) return null;
	if (isReleaseTab(value)) return value;
	return RELEASE_TAB_ALIASES[value] ?? null;
}

export function isSettingsTab(value: string | undefined): value is SettingsTab {
	return value != null && (SETTINGS_TABS as readonly string[]).includes(value);
}

export function isReleaseEditorSection(value: string | undefined): value is ReleaseEditorSection {
	return value != null && (RELEASE_EDITOR_SECTIONS as readonly string[]).includes(value);
}

/** Its artist twin - same reason, same shape (a predicate, never a cast). */
export function isArtistEditorSection(value: string | undefined): value is ArtistEditorSection {
	return value != null && (ARTIST_EDITOR_SECTIONS as readonly string[]).includes(value);
}

/**
 * One row of the dashboard's needs-attention strip - the round-70 key seam with a PICTURE beside it.
 *
 * ⚠ It lives here, and both sides import it, because round 146 changed its SHAPE. The frontend had
 * hand-mirrored this interface since round 120, which is the trap that round itself named ("a payload
 * the frontend re-declares by hand drifts silently") with the rule that the moment to stop mirroring
 * is the round the payload changes. The rest of the dashboard payload is still mirrored; it has not
 * had that moment yet.
 *
 * ⚠ `icon` is an `IconName`, not a glyph, and the reason the server picks it at all is that only the
 * server knows WHICH of ten alert kinds a row is - the strip is a heterogeneous list, and the mark is
 * how it is scanned. A uniform control does not want one: the mission card's `nextAction` is a single
 * button whose label IS the action, so it deliberately carries no icon field.
 */
export interface AttentionRow {
	/** Which drawing sits at the head of the row - a name both sides agree on, never a glyph */
	icon: IconName;
	/** i18n key of the row's sentence - see `services/dashboard.ts` on the key seam */
	textKey: string;
	/** Named parameters the sentence interpolates; a `count` also drives its CLDR plural form */
	textParams?: Record<string, string | number>;
	/**
	 * The second line under the headline (§AL, the "Needs you" card): WHY it is on the list, or what
	 * fixes it. Its own whole sentence, never a clause spliced onto the headline - the old rows ran the
	 * two together with " - " and read as one long machine sentence. Takes the same `textParams`.
	 */
	detailKey?: string;
	/**
	 * The detail's own parameters, when it counts something the headline does not. Only `count` can
	 * drive a plural, and the replies row counts replies in its headline and DAYS in its detail - which
	 * shared one set of params as "Oldest: 76d", an abbreviation standing in for a plural it could not
	 * pick. Absent reads as `textParams`.
	 */
	detailParams?: Record<string, string | number>;
	/**
	 * The verb on the row's button ("Answer", "Check the domain"), as a key. Absent reads as a plain
	 * "Open". A VERB rather than the destination's name, because the destination is already the whole
	 * row's click - the button's job is to say what the owner will DO when they get there.
	 */
	actionKey?: string;
	/**
	 * Only "warning" today, and only for the rows that are a PROMISE the app is not keeping rather
	 * than a task waiting for the owner. The distinction is the strip's whole ranking argument (§1),
	 * and position alone cannot carry it - a list of six neutral rows reads as six chores.
	 */
	severity?: "warning";
	/** SPA page the row deep-links to */
	goto: "sent" | "queue" | "campaigns" | "releases" | "settings" | "artists";
	/** Set when the row targets one release's workspace */
	releaseId?: number;
	/** Set when the row targets one ARTIST's workspace (round 162 - the link-in-bio page is theirs) */
	artistId?: number;
	/** Workspace subpage to land on - closed, so the server cannot name one that is not there */
	tab?: ReleaseTab;
	/**
	 * Settings subpage to land on, for a row whose fix lives there (round 178). Absent means the
	 * Settings page's own default, which is the honest answer for a row whose fix is not on a page at
	 * all - see the stalled-sweep row in `dashboard.ts`.
	 */
	settingsTab?: SettingsTab;
	/**
	 * A Settings sub-path to open on arrival - today only the plan chooser (2026-08-18).
	 *
	 * ⚠ **It exists because "lands on the tab that can fix it" was not enough for the one row whose
	 * fix is a PURCHASE** (owner report: the trial row *"takes me to Settings → Account and there is
	 * no clear CTA for me to claim the plan... which will lead to a drop in conversions"*). Round
	 * 178's rule was that a row must land where the lever is; on this screen the lever is behind one
	 * more button, and **the last click before a payment is the one a funnel actually loses.**
	 *
	 * ⚠ **A closed union of one, deliberately.** The frontend appends it to the sub-path
	 * (`/settings/account/plans`), which is a route that already existed for the trial-ended screen -
	 * so this is a second caller of one mechanism rather than a second mechanism, and a value the
	 * page does not answer to could never be typed.
	 */
	settingsSub?: "plans";
	/**
	 * A section of the release EDITOR to open on arrival, for a row whose lever is a field in that
	 * modal rather than anything on a page (§I.5 round 3). Read with `releaseId`, and `goto` stays
	 * `"releases"`; absent means the release workspace's own tab, which is the honest answer for every
	 * row whose fix really is on a page.
	 *
	 * ⚠ **A row that lands the owner NEAR the lever is the defect round 178 spent a round removing**
	 * from the five `goto: "settings"` rows, and it is the reason round 255 shipped the sync lane with
	 * no readiness row at all rather than one pointing at the release. Sending somebody to a screen
	 * where the thing cannot be done, with nothing broken-looking when they arrive, is worse than
	 * saying nothing: they go away believing they have checked.
	 *
	 * ⚠ **The frontend opens it AFTER the navigation, never as part of it** - `navigateTo` drops the
	 * dialog key on purpose, since a dialog belongs to the page you left. `pages/compose.ts`'s
	 * pre-flight button has carried that ordering comment since the mechanism shipped; the strip's
	 * jump now does the same thing for the same reason.
	 */
	dialog?: ReleaseEditorSection;
	/**
	 * A floating surface to OPEN rather than a page to navigate to (2026-08-07).
	 *
	 * ⚠ **It exists because a destination stopped being a page.** Help moved out of Settings and into
	 * `<gh-help-bubble>`, which is reachable from everywhere and is not a route - so the support row
	 * had nowhere to send anybody. The honest fix is for the payload to say WHICH KIND of action it
	 * wants, rather than the frontend recognising one particular `textKey` and quietly doing something
	 * else: a locale key is not an identifier, and matching on one is a coupling no gate can see.
	 *
	 * ⚠ It does not replace `goto`, which stays REQUIRED and stays the fallback. A floating surface
	 * can be absent - the bubble is only mounted for a signed-in app - and a row whose only
	 * destination has not been mounted is a row that does nothing when pressed.
	 */
	opens?: "help";
}

/**
 * Which evidence a momentum prompt is built on (round 183, `services/momentum.ts`).
 *
 * ⚠ A closed list rather than free strings, for round 144's reason: **you cannot notice a missing
 * entry in a list anything may add itself to.** Every kind owes two locale keys - `momentum.ev.<kind>`
 * and `momentum.act.<kind>` - and `tests/momentum.test.ts` walks this array to prove they exist in
 * both dictionaries, which is round 122's derived-family gate rather than a scan for literals. That
 * matters more here than on a busier surface: the lane is silent by design most of the time, so a
 * missing key would render as a raw key on the rare morning the owner most wants to read it.
 *
 * ⚠ It is the ARRAY that is declared and the union that is DERIVED (rounds 178/179): a hand-written
 * union beside a hand-written runtime list is only half-checked, and here the runtime list is the
 * thing the exhaustiveness gate walks - so an omission from it would silently narrow the gate to
 * whatever it happened to contain.
 */
export const MOMENTUM_KINDS = ["popularityUp", "unfeaturedMentions", "lookCommitment"] as const;
export type MomentumKind = (typeof MOMENTUM_KINDS)[number];

/**
 * THE LISTEN READING (§AL round 4, rule 03 of the spec: *one number leads, and it is listens*). What one
 * release's published pages did in the last 30 days, read off the page with the most visitors - the
 * sentence the Today page opens on, and the one a release's Insights tab opens on.
 *
 * ⚠ **`listens` is `EpkTotals.listens`, the app's ONE definition of somebody hearing the song** (a full
 * play plus a hook play), never a second count. `visitors` is distinct visitors who opened the page.
 * ⚠ **`downloads` is null where the page offers none** (the smart link) - a zero there would read as
 * "nobody downloaded" about a button that does not exist.
 */
export interface ListenReading {
	releaseId: number;
	title: string;
	artist: string;
	hasCover: boolean;
	page: "link" | "epk" | "kit";
	visitors: number;
	listens: number;
	downloads: number | null;
	/** The page's live address, for "copy the link" - null when it is not published right now */
	pageUrl: string | null;
}

/**
 * One release's card on the Releases list (the "Page by page" mockups, round 340): what a person can
 * tell from across the room - did anyone hear it lately, is it being pitched, and did that land.
 * ⚠ Every number is somebody else's: `reading` and `pages` are `listenReading`'s and
 * `releasePageReadings`', and the three counts are `releaseFunnel`'s, so the card can never disagree
 * with the Insights tab or the Campaign tab about the same release.
 */
export interface ReleaseCardReading {
	releaseId: number;
	reading: ListenReading | null;
	pages: PageReading[];
	/** First pitches sent (follow-ups are not a second outlet) */
	pitched: number;
	replied: number;
	covered: number;
}

/**
 * One of a release's three pages with the listen reading's own numbers (§AL, the release header's page
 * cards) - the same 30-day window and the same `listens`, since `listenReading` is the best of these.
 * `url` only while the page is live; `downloads` null on the smart link, which offers none.
 */
export interface PageReading {
	page: "link" | "epk" | "kit";
	live: boolean;
	url: string | null;
	visitors: number;
	listens: number;
	downloads: number | null;
}

/**
 * The pitch card on Today (§AL, the approved mockup's "Ripples outreach"): one release's outreach in
 * three numbers, for the release the owner pitched most recently. Per release rather than per 30 days
 * because the whole app is about a song - "2 outlets said yes to Ripples" is a sentence about the
 * work, "2 accepted in the last 30 days" is a sentence about a calendar.
 *
 * ⚠ **The three numbers are the Outreach tabs' own definitions**, so each one opens a tab that shows
 * the same rows: `sent` is `status = 'sent'`, `replied` is `replied_at IS NOT NULL`, `accepted` is a
 * row in `accepted_releases` (the owner's 2026-08-04 fix: "accepted" is an outlet saying yes, never a
 * published article - that is the coverage wall's number).
 */
export interface OutreachLead {
	releaseId: number;
	title: string;
	sent: number;
	replied: number;
	accepted: number;
	/** The newest outlets that said yes, newest first - at most three */
	recent: { outlet: string; acceptedAt: string }[];
}

/**
 * One prompt on the dashboard's momentum lane - what is WORKING, as opposed to the attention strip's
 * what is BROKEN (round 183, BACKLOG §I).
 *
 * ⚠ **It is deliberately not an `AttentionRow`, though the two render alike.** A strip row is a
 * promise the app is not keeping and its whole design - the three groups, the ranking, the warning
 * severity, the nav badge that counts warnings - exists to make it un-skimmable. A prompt here is an
 * opportunity, it carries no severity because there is nothing wrong, and it must never be counted
 * into that badge: a badge lit by good news is a badge nobody checks. Sharing the type would have
 * made all of that one `severity: undefined` away from being wrong.
 *
 * ⚠ **Two sentences, not one.** The EVIDENCE (the numbers and where they came from) and the ACTION
 * (what the owner might do about it) are separate `KeyText`s because they are separate sentences -
 * gluing them would be the concatenation rule broken in the one place a translator most needs the
 * whole clause, and because the evidence is a claim the app is making and the action is only ever a
 * suggestion. Keeping them apart on screen is also what makes the lane readable at a glance.
 */
export interface MomentumPrompt {
	kind: MomentumKind;
	/** The mark the row is scanned by - a heterogeneous list, so the server picks it (round 146) */
	icon: IconName;
	/** What the numbers say. Decided here, said by the reader (the round-70 seam). */
	evidence: KeyText;
	/** What to do about it - a recommendation. Nothing on this lane ever acts. */
	action: KeyText;
	/** SPA page the prompt deep-links to */
	goto: "releases" | "content";
	/** Set when the prompt targets one release's workspace */
	releaseId?: number;
	/** Workspace subpage to land on - closed, so the server cannot name one that is not there */
	tab?: ReleaseTab;
}

/**
 * \`GET /api/settings/socials\` - one artist's social connections, what each of them can DO, and the
 * values a credential form shows back.
 *
 * ⚠ **It lives here because TWO surfaces read it now**: \`<gh-artist-connections>\` (Instagram,
 * YouTube, TikTok, Facebook - in the artist workspace, where a per-artist credential belongs) and
 * \`<gh-settings-page>\` (Meta Ads, which stays workspace-wide chrome). That is exactly the trigger
 * \`src/frontend/CLAUDE.md's\` mirror rule names - *a payload the frontend mirrors should be imported
 * from here the moment that payload changes shape* - and a second copy of a shape with ten
 * credential fields would typecheck green on both sides while one of them rendered \`undefined\`.
 *
 * ⚠ **\`values\` is what was SAVED, never what the environment holds**, and in saas the operator's own
 * app credentials come back empty strings rather than masked - see \`tests/platformAppSaas.test.ts\`,
 * which is the canary for that, and \`docs/notes/social.md\` on the echoed-credential leak.
 */
export interface SocialsState {
	instagram: { connected: boolean; username: string | null; canPublish: boolean | null };
	youtube: { configured: boolean; channel: string | null };
	tiktok: {
		handle: string | null;
		appConfigured: boolean;
		connected: boolean;
		displayName: string | null;
		canUpload: boolean | null;
		canListVideos: boolean | null;
		relayUrl: string | null;
	};
	facebook: { connected: boolean; pageName: string | null; canPublish: boolean | null };
	metaAds: { connected: boolean; account: { id: string; name: string; currency: string } | null };
	lastSync: { at: string; refreshed: number; created: number; failed: number } | null;
	artists?: { id: number; name: string }[];
	artistId?: number | null;
	values: {
		instagramToken: string;
		youtubeApiKey: string;
		youtubeChannel: string;
		tiktokHandle: string;
		tiktokClientKey: string;
		tiktokClientSecret: string;
		facebookPageId: string;
		facebookPageToken: string;
		metaAdsAccountId: string;
		metaAdsToken: string;
	};
	/**
	 * Whose developer apps these connections sign into (round 139). `owner: "operator"` is the
	 * hosted edition, where the tenant never sees an app-credential field at all - the per-platform
	 * booleans then say whether the operator has actually finished setting that app up, which is
	 * the difference between offering a Connect button and saying why there isn't one.
	 */
	apps: { owner: "install" | "operator"; instagram: boolean; meta: boolean; tiktok: boolean; youtube: boolean };
}

// ── The INSIGHTS page (round 205, BACKLOG §N.5, `services/insights.ts`) ──────────────────────
// The landing page's strongest claim - "the part you cannot get anywhere else" - names four things
// and the app had no surface called any of them. These types are the address.

/**
 * The artist workspace's subpages, in display order.
 *
 * ⚠ **It lived in `pages/artists.ts` until this round, and the comment there said exactly why: no
 * server payload named an artist tab, and sharing a type nothing on the other side reads is the dead
 * abstraction round 73 warns about.** That reason expired the moment an `InsightGoto` could point at
 * the Compare tab - the condition, not the type, is what changed. **A justification can expire
 * without the thing it justified changing**, so re-derive it when the surface moves rather than
 * inheriting it.
 *
 * The KEYS live here and the LABELS stay in the island, which keeps the property the old derivation
 * had: `Record<ArtistTab, string>` fails to compile when a tab has no label, so a hand-written union
 * beside a hand-written list can never be half-checked.
 */
/**
 * ⚠ `connections` joined on 2026-08-11, when the four PLATFORM connectors moved here out of
 * Settings → Connections (owner ask). It sits last because it is setup rather than work: the other
 * five are things the owner does about this artist every week, and this one is a thing they do once.
 * See `components/artistConnections.ts` on why Meta Ads and the mail lanes stayed behind.
 */
export const ARTIST_TABS = ["profile", "bio", "audience", "compare", "photos", "connections"] as const;
export type ArtistTab = (typeof ARTIST_TABS)[number];

/**
 * The four claims the front door makes, in the order it makes them. A CLOSED union for round 144's
 * reason: the page's job is to answer for every claim on that page, and a claim quietly missing
 * from a surface built to demonstrate them is the one failure nobody can see. `tests/insights.test.ts`
 * walks this array, so a fifth promise on the landing page has to arrive here to render at all.
 */
export const INSIGHT_CLAIMS = ["own", "across", "song", "next"] as const;
export type InsightClaim = (typeof INSIGHT_CLAIMS)[number];

/**
 * The marks those claims are scanned by - a CLOSED union, and NOT the whole 150-name icon set.
 *
 * ⚠ **The narrowing exists because the page renders these as EMPTY-STATE DRAWINGS** (2026-08-08), and
 * the drawings are gated by `tests/emptyArt.test.ts`, which reads string LITERALS at call sites in
 * `src/frontend`. The insights island calls `emptyState(card.icon, …)`, so the name it renders was
 * chosen HERE, on the server, and the scanner sees nothing at all - which is `shared/iconNames.ts`'s
 * own founding problem one level down: **a payload field is a call site the gate cannot see.** An
 * `IconName` field is unguardable for this purpose, because requiring a drawing for all 150 would be
 * absurd and requiring one for none is what let three of these four cards render a fallback glyph.
 *
 * So the field holds a name from this list, the gate imports the list, and a fifth claim carrying a
 * new mark fails the suite until someone draws it. ⚠ **The looseness left is in the other direction**:
 * nothing checks that every name here is still ASSIGNED by `services/insights.ts`, so a retired claim
 * would leave an entry behind and keep its drawing looking used. That is the cheap direction to be
 * wrong in - it costs an unused drawing, where the other costs a card that silently looks unfinished.
 */
export const INSIGHT_ICONS = ["target", "share", "waves", "compass"] as const satisfies readonly IconName[];
export type InsightIcon = (typeof INSIGHT_ICONS)[number];

/**
 * Where a claim is actually WORKED, in full. A discriminated union rather than a page name plus
 * loose ids, because the three destinations take different arguments and a card that names a
 * release tab while pointing at the Content page is a dead link the types should have refused.
 */
export type InsightGoto =
	| { page: "content" }
	| { page: "release"; releaseId: number; tab: ReleaseTab }
	| { page: "artist"; artistId: number; tab: ArtistTab };

/**
 * One claim, with the evidence THIS workspace can show for it.
 *
 * ⚠ **`evidence` and `missing` are two fields, and exactly one of them is set.** This page is
 * navigated TO, so round 184's rule binds rather than round 183's: answering nothing reads as
 * broken, and the honest answer on day 3 is what would fill it. A single nullable sentence would
 * have let a card render a blank where "connect an account and post twice" belongs.
 *
 * ⚠ **The evidence is the app's own reading, never a benchmark.** Every number on this page is
 * scored against the artist's own history - that IS the claim - so nothing here may acquire a
 * comparison against other tenants, however tempting an average becomes once there are enough of
 * them to compute one.
 */
export interface InsightCard {
	claim: InsightClaim;
	/**
	 * The mark the card is scanned by - a heterogeneous list, so the server picks it (round 146).
	 * ⚠ An `InsightIcon`, not any `IconName`: the island draws it as an empty-state ILLUSTRATION, and
	 * that closed union is the only thing a gate can hold to having one. See its doc above.
	 */
	icon: InsightIcon;
	/**
	 * What this workspace's own data says. Empty when it has nothing to say yet.
	 *
	 * ⚠ **A LIST, because a claim can rest on more than one reading and the front door's fourth one
	 * openly does**: "which look, which length and which hour your audience turns up for. And how this
	 * release is doing against the last one" is two different questions under one heading. Round 205's
	 * first cut had a single `KeyText` and would have had to drop one of them, pick arbitrarily, or
	 * concatenate two decided sentences into a third - and that last one is the concatenation rule
	 * broken exactly where a translator most needs whole clauses.
	 */
	evidence: KeyText[];
	/** What would fill it. Set exactly when `evidence` is empty. */
	missing: KeyText | null;
	/** The surface that owns the claim in full - null when there is nowhere useful to send them yet. */
	goto: InsightGoto | null;
}

export interface InsightsPayload {
	/** The artist every card is scoped to, or null when the workspace has none. */
	artistId: number | null;
	/** `has_avatar` rather than the image: the page's picker draws each artist's photo (`pickerRows.ts`) */
	artists: { id: number; name: string; has_avatar: boolean }[];
	cards: InsightCard[];
	/**
	 * Platforms that are connected but cannot report a view count (the IG/FB wall).
	 *
	 * ⚠ It is part of the CLAIM, not a caveat: the front door promises "where a platform will not
	 * tell us a number, it says so instead of guessing", so a page demonstrating that promise has to
	 * do it out loud. Empty means every connected platform reports - which is a different sentence
	 * from having nothing connected, and the reader must be able to tell those apart.
	 */
	unmeasured: string[];
}

// ── The Audience view (round 184, `services/audience.ts`) ────────────────────────────────────
// Shared because both sides read it, which is round 120's rule: a payload the frontend re-declares
// by hand drifts silently, and the moment to stop mirroring is the round the payload is written.

/**
 * A counted breakdown of an audience, with its two kinds of remainder kept APART.
 *
 * ⚠ `other` is "we could name these and chose not to show them" - the tail past the Top-N. `unknown`
 * is "we cannot name them at all" - a signup with no `?src=` tag, no country, no language on the
 * row. Pooling the two into one "other" gives a number that is true of neither, and a reader who
 * sees a single remainder reads it as the second kind, which quietly turns a display cap into a
 * claim about the data. Two fields so a renderer cannot say one and mean both (round 150: prefer a
 * type where the class of mistake has a shape one can express).
 */
export interface AudienceBreakdown<T> {
	rows: T[];
	other: number;
	unknown: number;
}

/** One clip's credit, at the ARTIST level - see the note on tag uniqueness in `audience.ts` */
export interface AudienceSourceRow {
	/** The `?src=` tag exactly as the clip carried it */
	source: string;
	/** …and the release it belongs to: a tag is only unique WITHIN one (round 127) */
	releaseId: number;
	releaseTitle: string;
	count: number;
}

export interface AudienceReleaseRow {
	releaseId: number;
	title: string;
	/** Null for a release with no date - the view sorts those last rather than guessing */
	releaseDate: string | null;
	count: number;
}

/** What the artist's Audience tab draws. No addresses, on purpose - see `services/audience.ts`. */
export interface AudienceSummary {
	/** Everyone who ever signed up for any release of this artist */
	total: number;
	/** …of whom still have an address (round 136 keeps the row and blanks the address) */
	reachable: number;
	/** Signed up, and the release-day message has not gone to them yet */
	waiting: number;
	/** On the artist's standing list with an address - they hear about every new release (round 297) */
	list: number;
	/** Signed up on the link-in-bio page, which has no release to file them under (round 297) */
	fromBioPage: number;
	/** New signups per UTC day over the last 30, oldest first, zero-filled */
	daily: number[];
	/** Signups in the last 30 days, and in the 30 before those - the honest delta */
	last30: number;
	prev30: number;
	releases: AudienceReleaseRow[];
	sources: AudienceBreakdown<AudienceSourceRow>;
	/** ISO-3166 alpha-2 as the beacon's geo gave it; the READER says the country's name */
	countries: AudienceBreakdown<{ code: string; count: number }>;
	/** The base language tag from `navigator.language` at signup; the reader says its name */
	languages: AudienceBreakdown<{ code: string; count: number }>;
}

/**
 * RELEASE-OVER-RELEASE COMPARISON (round 185, BACKLOG §I) - see `services/releaseCompare.ts` for
 * the reasoning behind every field here.
 *
 * The four groups are the app's four INSTRUMENTS, and that is what makes them the grouping rather
 * than a tidy heading: everything in one group goes dark together, because it is one lane being
 * live or not (the pitch tracker, the beacon, the signup form, the Spotify sync).
 */
export const COMPARE_GROUPS = ["outreach", "page", "commitment", "streaming"] as const;
export type CompareGroup = (typeof COMPARE_GROUPS)[number];

/** The axes, in the order they render - a funnel, top to bottom. */
export const COMPARE_AXES = ["pitches", "replies", "placements", "visits", "listens", "clicks", "signups", "popularity"] as const;
export type CompareAxisKey = (typeof COMPARE_AXES)[number];

/**
 * How an axis's numbers may be compared.
 *
 * ⚠ `index` exists for exactly one reason and it is a correctness one: Spotify popularity is a
 * 0–100 RELATIVE ranking, not a count of anything, so its change is expressed in POINTS and never as
 * a percentage - 8 → 10 rendered as "+25%" is a claim about listening that the number cannot carry
 * (BACKLOG's own warning on this entry, and round 135's true-and-misleading rule pointed at an axis
 * label). A `count` axis is a tally of things that happened and takes a ratio honestly.
 */
export type CompareAxisKind = "count" | "index";

/** The newest campaign against the one before it, on one axis. Absent below the floor. */
export interface CompareVerdict {
	/** newest − previous, in the axis's own unit (things, or index POINTS) */
	delta: number;
	/** (newest − previous) ÷ previous - null on an `index` axis, and when the baseline is 0 */
	ratio: number | null;
	direction: "up" | "down" | "level";
}

export interface CompareAxis {
	key: CompareAxisKey;
	group: CompareGroup;
	kind: CompareAxisKind;
	/**
	 * One value per compared release, in `ReleaseComparison.releases` order.
	 *
	 * ⚠ **`null` means the app was not recording, and it may never be drawn as a zero.** A campaign
	 * that ran before the beacon existed, before the signup form shipped or before the release row
	 * was in the database did not fail at those things - nothing was watching. See the module header.
	 */
	values: (number | null)[];
	verdict: CompareVerdict | null;
}

/** A release that could not be lined up, and the reason - never silently dropped. */
export interface CompareExclusion {
	releaseId: number;
	title: string;
	reason: KeyText;
}

export interface ReleaseComparison {
	/**
	 * The window every column is measured over, in days relative to each release's own release day.
	 * `start` is the runway's first rung; `end` is the youngest compared release's own age, so no
	 * release is credited for days another has not lived yet.
	 */
	window: { start: number; end: number };
	/** Newest first. Fewer than two means there is nothing to compare yet. */
	releases: { releaseId: number; title: string; releaseDate: string; ageDays: number }[];
	/** Only axes at least one release has a real number for; an all-unknown axis is dropped. */
	axes: CompareAxis[];
	excluded: CompareExclusion[];
}

/**
 * Where one runway rung stands for one release (`buildRunway` in services/releaseRunway.ts). Shared
 * since round 324, when it changed shape: the Runway island had its own copy of this object, and a
 * mirror is only safe until the day a field is added to one side of it.
 */
export interface RunwayRungStatus {
	rendered: boolean;
	/** A publish-queue row (draft/scheduled/publishing/awaiting_finish) exists for this rung's clip */
	queued: boolean;
	posted: boolean;
	/** Due date has passed, nothing went up, and it is still inside its window - the ladder's "you are here" marker */
	overdue: boolean;
	/**
	 * Past even its window (the grace days in releaseRunway.ts), with nothing posted. ⚠ Round 324: a
	 * release 70 days out drew all eight rungs red and "overdue", each with a Render button, while the
	 * autopilot card above them said nothing was left to post. Both were reading the same rungs and
	 * answering "is this still worth posting" separately. A missed rung is quiet and never nags; it
	 * can still be rendered by hand.
	 */
	missed: boolean;
	/** The owner waved this rung off for this release - faded, never overdue */
	ignored: boolean;
	/** The rendered clip's tag, when one exists - what "Mark posted" logs under */
	source: string | null;
	/**
	 * Strangers this rung's clips talked into the release-day list (round 119). The ladder's only
	 * feedback used to be "did it go up?"; this is the first thing on it that says whether a rung
	 * WORKED, and it says it in commitments rather than impressions.
	 */
	signups: number;
}

/** One line of the armed card's plan: what the autopilot will do for a rung, or why it won't */
export interface AutopilotPlanEntry {
	key: string;
	/** The server's ENGLISH rung title - the manifest join key; the island translates off `key` */
	title: string;
	due: string | null;
	note: AutopilotText;
	/** Why this rung is stuck, when it is - cleared on the next successful pass */
	error: AutopilotText | null;
}

export interface AutopilotStatus {
	armed: boolean;
	armedAt: string | null;
	postHour: number;
	/** Why arming is refused - empty when armed or armable */
	blockers: AutopilotText[];
	/** False = captions will be plain factual announcements, said up front */
	aiReady: boolean;
	/** The Instagram account it posts as, when known */
	username: string | null;
	/** Why the armed tick is standing still - derived per read, never stored */
	pausedReason: AutopilotText | null;
	plan: AutopilotPlanEntry[];
}

/**
 * Playlist trust audit (round 107, recovered from the round-28 idea backlog in the round-55 audit).
 *
 * Before an artist pitches - or pays - a Spotify playlist curator, this vets the playlist for
 * payola-farm and botted-audience signals. It matters because a placement on a farmed playlist does
 * not merely fail to convert: bot streams can actively HURT the artist's algorithmic standing, so
 * the honest move is to warn BEFORE the pitch, never to count a poisoned placement as a win. (The
 * same §1 test as everything else - a "play" that is not a real listen is worse than no play.)
 *
 * Like preflight (round 70), this module NAMES i18n KEYS, it does not write prose: the server
 * decides which signals are TRUE (a `code` + `params`), the frontend SAYS them through `t()`. A
 * signal composed as an English sentence on the server could reach neither i18n seam.
 *
 * It is LAYERED by Spotify API tier, and honest about the ceiling. Description and follower signals
 * work on every app. The strongest behavioral signals (playlist bloat, bulk "submission-batch"
 * adds, the "lots of followers but the songs get zero plays" tell) need the playlist's track
 * listing, which Spotify's default post-Nov-2024 app tier answers with 403 - so the report carries
 * `trackDataAvailable`, and when it is false the UI says which wall it hit rather than implying a
 * playlist is clean when we simply could not look.
 */
export type TrustSeverity = "avoid" | "caution" | "info";

/** The one-glance answer to "should I pitch this curator?" */
export type TrustVerdict = "trusted" | "caution" | "avoid" | "unknown";

export interface TrustSignal {
	/** i18n key of the one honest sentence for this signal ("trust.sig.paidSubmission") */
	code: string;
	severity: TrustSeverity;
	/** Named parameters the sentence interpolates (a platform name, a percentage, a count) */
	params?: Record<string, string | number>;
}

export interface PlaylistTrustReport {
	/** The Spotify playlist id, or null when the stored URL was not a playlist link */
	playlistId: string | null;
	name: string;
	url: string;
	followers: number | null;
	verdict: TrustVerdict;
	/** 0–100, higher is safer - a numeric handle for sorting and future ROI weighting */
	score: number;
	signals: TrustSignal[];
	/** False when the track listing was walled off (default-tier 403) - the UI names the wall */
	trackDataAvailable: boolean;
}

/** The subset cached on the blog row and sent with the blogs list, so a badge needs no fetch */
export interface CuratorTrustSummary {
	verdict: TrustVerdict;
	score: number;
	/** Curator-level signals: the union across the curator's playlists, deduped by code (worst wins) */
	signals: TrustSignal[];
	/** ISO timestamp the audit last ran; null when never audited */
	checkedAt: string | null;
}

export interface CuratorTrustReport extends CuratorTrustSummary {
	blogId: number;
	/** Per-playlist breakdown - a curator may run several, and only one may be the farm */
	playlists: PlaylistTrustReport[];
}

/**
 * Decline-feedback intelligence (round 57, recovered from the round-28 idea backlog in the round-55
 * audit). Every polite "no" an outlet sends carries market intel about the SONG - but scattered
 * across dozens of replies it reads as noise. This turns the pile of declines for one release into a
 * pattern ("3 of 7 outlets who said why point at the mix") the artist can act on before the next
 * pitch. It is about why OUTLETS pass on the song, so it deliberately ignores rejections the ARTIST
 * closed (a declined paid-placement offer is not feedback on the music).
 */
export interface DeclineSignal {
	/** The outlet that passed */
	outlet: string;
	/** What they said, in one line - an outlet reply's AI triage summary, or a hand-typed rejection note */
	text: string;
	/** Where the line came from: a real reply we triaged, or a manually marked rejection */
	source: "reply" | "marked";
}

export interface DeclineTheme {
	/** Short human label for the pattern ("The mix / production", "Not their genre right now") */
	theme: string;
	/** How many of the reasoned declines express this theme (distinct outlets) */
	count: number;
	/** The outlets whose message fed this theme - the "who" behind the count */
	outlets: string[];
	/** One plain, actionable sentence: what to change before the next pitch. Never hype, never consolation. */
	note: string;
}

export interface DeclineFeedback {
	releaseId: number;
	title: string;
	/** Every outlet that passed on the song, however it was recorded */
	totalDeclines: number;
	/** Declines that came with something to read - the ones the pattern-finder can work with */
	withReason: number;
	/** Declines with nothing to read: counted, but they can't tell you why */
	silent: number;
	/** The raw one-liners, gathered deterministically - shown before (and without) the AI pattern-find */
	signals: DeclineSignal[];
	/** True once the AI clustering has run; false on the cheap GET that only gathers the raw lines */
	analyzed: boolean;
	/** One honest headline naming the dominant pattern with its real count, null until analyzed */
	headline: string | null;
	/** The clustered themes, most-common first; empty until analyzed */
	themes: DeclineTheme[];
}

// ── Submission-channel ROI (round 58, a recovered round-28 idea) ──
// A paid submission platform is how you buy a curator's attention when a cold email won't earn it -
// and every submission costs money whether it places or not. The board answers the only honest
// question: which channel actually turns spend into placements, measured against the free baseline
// (direct email). Shared so the service and the page agree on every field.

/** How a logged submission turned out. Only "placed" counts toward a placement; "pending" = paid, still waiting. */
export type SubmissionOutcome = "pending" | "placed" | "declined";

export interface SubmissionLog {
	id: number;
	release_id: number;
	/** The channel key: a SUBMISSION_CHANNELS value, or a free-typed platform name for "other" */
	channel: string;
	/** The curator / playlist / blog on that platform (free text, optional) */
	outlet_name: string | null;
	/** What this submission cost, in minor units (cents/øre) of the workspace promo_currency */
	cost_cents: number;
	submitted_at: string;
	outcome: SubmissionOutcome;
	/** Where the placement landed (playlist add, feature URL) - only meaningful once placed */
	placement_url: string | null;
	/** The KIND of placement won ("playlist", "feature", "story", "video", "radio", …) - the quality half of the comparison */
	placement_kind: string | null;
	/** The curator's written feedback, when they gave any (SubmitHub does) */
	feedback: string | null;
	notes: string | null;
	created_at: string;
	updated_at: string;
}

/** One channel's line on the ROI board - a logged platform, or the derived free "direct" lane. */
export interface ChannelRoi {
	/**
	 * Channel key ("direct" for the derived email lane, else a SUBMISSION_CHANNELS value / custom
	 * name). The row's HEADER used to ride along beside it as a server-composed English `label`;
	 * the reader can compute it from this key with `channelLabel()`, so it is derived rather than
	 * sent (round 121's rule - a stored or shipped copy of a fact the reader can check only ever
	 * buys a way for the two to disagree).
	 */
	channel: string;
	/** True for the derived free lane (direct email); its spend is always 0 and it can't be edited */
	derived: boolean;
	/** Attempts: pitches sent (direct) or submissions logged (paid) */
	submissions: number;
	/** Wins: coverage accepted (direct) or submissions marked placed (paid) */
	placements: number;
	/** Passed on: outlet rejections (direct) or submissions marked declined (paid) */
	declined: number;
	/** Still open: contacted/submitted with no verdict yet */
	pending: number;
	/** Total spent on this channel, in minor units - always 0 for the derived lane */
	spent_cents: number;
	/** spent_cents / placements, in minor units; null when nothing placed (dividing by zero would lie) */
	cost_per_placement_cents: number | null;
	/** placements / submissions, 0..1; null when nothing was attempted */
	placement_rate: number | null;
}

export interface SubmissionRoiBoard {
	releaseId: number;
	title: string;
	/** ISO 4217 code the money in this board is denominated in (workspace promo_currency, default USD) */
	currency: string;
	/** One row per channel, most placements first, with the free "direct" lane always present */
	channels: ChannelRoi[];
	/** The raw paid-platform log, newest first - direct-email placements live in the funnel, not here */
	logs: SubmissionLog[];
}

/**
 * The paid submission platforms the log offers as presets. "other" opens a free-text field so a
 * platform we didn't list (or a one-off curator submission portal) is still trackable. The DERIVED
 * free lane uses the reserved key "direct" and is never one of these. Kept in shared so the page's
 * dropdown and the service can't drift.
 *
 * A row carries EITHER a `brand` or a `labelKey`, never both, because the two are different kinds
 * of string: a platform's name is a proper noun and stays "SubmitHub" in every language (the same
 * call `shared/brand.ts` makes about our own wordmark), while "Other platform" is app chrome and
 * belongs in the dictionary. Naming the fields apart is what keeps the distinction from rotting -
 * a single `label:` invited exactly one English sentence to hide among six proper nouns, which is
 * where round 122's class-5 audit found it.
 */
export const SUBMISSION_CHANNELS: { value: string; brand?: string; labelKey?: string }[] = [
	{ value: "submithub", brand: "SubmitHub" },
	{ value: "groover", brand: "Groover" },
	{ value: "musosoup", brand: "MusoSoup" },
	{ value: "playlistpush", brand: "Playlist Push" },
	{ value: "soundcampaign", brand: "SoundCampaign" },
	{ value: "dailyplaylists", brand: "Daily Playlists" },
	{ value: "other", labelKey: "roi.channel.other" }
];

/** The reserved key for the derived free lane (direct email / organic coverage). Never a stored channel. */
export const DIRECT_CHANNEL = "direct";

/**
 * How to render a channel key - `{ key }` when the label is chrome the reader's locale owns,
 * `{ text }` when it is a proper noun or the free text the owner typed into "other". The caller
 * resolves it, because only the caller's side knows the locale (the round-70 seam; `AutopilotText`
 * is the same idea for whole sentences). It deliberately does NOT return a string: a function that
 * sometimes hands back English and sometimes a key is one nobody can call correctly.
 */
export type ChannelLabel = { key: string } | { text: string };
export function channelLabel(channel: string): ChannelLabel {
	if (channel === DIRECT_CHANNEL) return { key: "roi.channel.direct" };
	const preset = SUBMISSION_CHANNELS.find(c => c.value === channel);
	if (preset?.labelKey != null) return { key: preset.labelKey };
	return { text: preset?.brand ?? channel };
}

/**
 * What an outlet's learned adjustment is MADE of - the evidence behind "Track +8", as counts.
 *
 * It used to travel as a server-composed English sentence stored on the row ("covered 1 release,
 * replied 2×, 3 unopened"), which round 125 deleted rather than converted: every number in it is
 * already a column of `outlet_track_record`, so the READER can compute the sentence, and round 121's
 * rule says derive it then - a stored sentence is a second source of truth that can only go stale.
 * The tooltip is composed in the reader's locale by `frontend/trackEvidence.ts`, which all three
 * surfaces that show it (Blogs chip, Compose fit, Queue fit) call, so they cannot word it differently.
 */
export interface TrackEvidenceCounts {
	replies: number;
	coverage: number;
	rejections: number;
	epkViews: number;
	trackedUnopened: number;
}

// ── Discovered mentions (round 30, the key seam applied round 124) ────────────

/**
 * The two discovery kinds the app COMPOSES rather than quotes: a referrer spike and a backlink are
 * read out of the beacon's own traffic (`services/shareSignals.ts`), so there is no post and nobody's
 * words - the card's sentence is ours. Every other kind carries a real human's text, which is never
 * translated and never a key.
 *
 * Exported because three places must agree on the distinction: the store (which field the sentence
 * goes in), the card (which one it renders), and the published-page wall (which rows it must refuse -
 * a wall headed "What people are saying" cannot quote the app back at a stranger).
 */
export const SIGNAL_MENTION_KINDS: readonly string[] = ["traffic", "backlink"];

/** One discovered mention as the Content page reads it */
export interface UgcMention {
	id: number;
	platform: string;
	url: string | null;
	author: string | null;
	/** The poster's OWN words - always empty for a signal kind, which has none */
	text: string;
	/**
	 * The app's own sentence about a signal kind, null for a real post (round 124). It is a
	 * `StoredText` and not a string because it is read back out of the `text` column, where rows
	 * written before that round hold the English the sweep composed at the time.
	 */
	summary: StoredText | null;
	thumb: string | null;
	postedAt: string | null;
	kind: string;
	views: number | null;
	likes: number | null;
	comments: number | null;
	/** A deliberate tag/@-mention/repost (from your network), vs a passive hashtag/sound hit */
	direct: boolean;
	/** The sweep's answer to "why do you think this is about me?" - null on pre-v85 rows */
	via: StoredText | null;
	/** The owner has pinned this to the social-proof wall on the published pages (v106) */
	featured: boolean;
	release: { id: number; title: string } | null;
}

/**
 * The currencies the ROI board can be denominated in. Deliberately all two-decimal (100 minor units)
 * currencies, because a submission's cost is stored as `amount × 100` cents - a zero-decimal currency
 * like JPY would make that arithmetic wrong. USD is the default when a workspace hasn't picked one.
 */
export const PROMO_CURRENCIES: string[] = ["USD", "EUR", "GBP", "DKK", "SEK", "NOK", "CHF", "PLN", "CAD", "AUD", "BRL", "MXN", "INR"];

// ── Best times to post (round 59) ─────────────────────────────────────────────
// A DERIVED, free read over the same content inventory the Content page already
// fetches: it answers "when have my posts actually reached people?" WITHOUT a raw
// view count - every post is scored as its index against the artist's OWN median
// on THAT platform (the identical math the content cards wear), so a TikTok slot
// and an Instagram slot are one comparable currency. Two independent marginals
// (day-of-week, part-of-day) rather than a 168-cell grid, because a single owner's
// post history is too sparse to cut two ways at once and stay honest.

/** The five parts of the day a post's local hour falls into */
export type TimeOfDayBand = "morning" | "midday" | "afternoon" | "evening" | "night";

export interface PostTimingBucket {
	/** Stable key: a weekday key ("mon"…"sun") or a TimeOfDayBand - what the UI switches on */
	key: string;
	/** JS getDay() index (0=Sun…6=Sat) for a weekday bucket, -1 for a time-of-day band. Lets the
	 *  UI render the weekday NAME through Intl (whose word it is to translate, never ours). */
	dayIndex: number;
	/** Posts with a timestamp that fell in this bucket, across every connected platform */
	posts: number;
	/** Of those, how many carried an honest index (a platform baseline to score against) */
	scored: number;
	/** Median index over the scored posts - null until `scored` clears the confidence floor, so a
	 *  bucket is never recommended off one lucky post */
	index: number | null;
	/**
	 * The median local hour (0–23) of the SCORED posts in this bucket - where the bucket's evidence
	 * actually came from - or null when it has none (round 163).
	 *
	 * ⚠ It is what turns a band into a number a scheduler can use, and the alternative was worse: a
	 * band's midpoint is an hour the artist may never have posted at, while this one is an hour that
	 * demonstrably reached their audience. The band is the resolution of the EVIDENCE; this is where
	 * inside it the evidence sits. On a weekday bucket it is a fact rather than a recommendation (a
	 * weekday spans the whole clock), and nothing reads it there.
	 */
	hour: number | null;
}

export interface PostTimingBoard {
	/** Posts with a parseable timestamp - the denominator the buckets are cut from */
	totalPosts: number;
	/** Of those, how many carried an honest index */
	scoredPosts: number;
	/** Mon-first weekday buckets (always 7, in display order) */
	byDayOfWeek: PostTimingBucket[];
	/** The parts of the day (always 5, in clock order) */
	byTimeOfDay: PostTimingBucket[];
	/** The best-scoring bucket on each axis - null until one clears the confidence floor */
	bestDay: PostTimingBucket | null;
	bestTimeOfDay: PostTimingBucket | null;
	/** False → the panel shows "post more, then check back", never a recommendation off thin data */
	enoughData: boolean;
	/** IANA zone the timestamps were bucketed in (the server's local clock), so the panel names it */
	timezone: string;
}

/**
 * What the 🤖 autopilot's arm dialog should DEFAULT its post hour to, and why (round 163).
 *
 * The board above is a read the owner looks at on the Content tab; this is the same evidence turned
 * into the one number the robot needs. It is a RECOMMENDATION and says so - the picker still lists
 * all 24 hours, the reason and the sample size ride along, and `null` (no advice) is a first-class
 * answer that leaves `DEFAULT_POST_HOUR` exactly where it was.
 *
 * ⚠ There is deliberately no day here. The runway's dates are a plan the owner can see and published
 * against; a robot quietly moving release day to a Thursday because Thursdays do well is the app
 * overruling the artist's calendar. The hour inside a day it already owns is a different thing.
 */
export interface PostHourAdvice {
	/** The hour (0–23) to preselect, in `timezone` */
	hour: number;
	/** Which part of the day the evidence is in - the resolution the data actually supports */
	band: TimeOfDayBand;
	/** How many SCORED posts stand behind it, so the owner can weigh the claim */
	scored: number;
	/** How far above the artist's own median this band runs, as a percentage (34 = +34%) */
	percent: number;
	/** The zone `hour` is a claim about - a wall-clock recommendation with no clock beside it is noise */
	timezone: string;
	/** The whole sentence, decided here and SAID by the reader (the round-70 key seam) */
	reason: KeyText;
}

export type LookVerdict = "over" | "under" | "flat" | "thin";

/**
 * How one look FAMILY is doing (round 42 lane #5, `services/crosspostScore.ts`), on the app's TWO
 * kinds of number - and the second one is why this type lives here rather than in the service.
 *
 * `index` is attention: pooled views ÷ pooled per-platform baselines, the same index-vs-your-own
 * reading every content card wears. `commitmentIndex` is round 119's other kind entirely - how
 * often a view of this look ended in a stranger typing their address to be told when the song is
 * out. §1 ranks them: "when a metric and a listen point in different directions, follow the
 * listen", and a signup is the closest thing this app measures to a promise to listen. So the
 * headline can name a look that wins on views and still tell the owner to make the other one.
 *
 * The verdict fields are computed server-side with the shared OVER/UNDER thresholds and the chip
 * is colored off them - the client never re-thresholds an index, which would be a third copy of
 * 1.25x/0.8x to drift out of step (see the crosspostScore/content.ts gotcha).
 */
export interface LookScore {
	look: string;
	/** i18n key for the family's name ("look.fam.footage") - the round-70 seam, resolved by the reader */
	labelKey: string;
	/** Pooled views ÷ pooled baselines; null when the look is too thin to score honestly */
	index: number | null;
	/** Posts of this look with an honest baseline - the ones `index` is actually built from */
	scoreablePosts: number;
	/** Every post logged under this look, scoreable or not - so "3 posts, still thin" can be said */
	totalPosts: number;
	verdict: LookVerdict;
	/** Release-day signups this look's clips brought in, over all of its `?src=` tags */
	signups: number;
	/** This look's signups-per-view ÷ the same rate pooled over every look; null when too thin */
	commitmentIndex: number | null;
	/** OVER/UNDER applied to `commitmentIndex`; "thin" whenever that is null */
	commitmentVerdict: LookVerdict;
}

export interface LookPerformance {
	/** Best-earning first; thin looks sink to the bottom but stay visible (the app IS tracking them) */
	looks: LookScore[];
	/** The one-line studio nudge as a key the reader says, or null when nothing is scoreable yet */
	headline: KeyText | null;
}

// ── The operator console (round 143, BACKLOG A.8) ────────────────────────────
// Declared HERE rather than in the route, because round 120's finding applies with force to a
// payload nobody outside the operator will ever look at twice: a hand-mirrored interface
// typechecks green on both sides while rendering `undefined`, and this is the one surface whose
// job is to be trusted about numbers.

/**
 * The activation funnel's stages, in order, ending at the moment the product has done its job.
 *
 * ⚠ The last stage is `audible`, not `published`, and that is the whole design. §1's rule is that
 * a surface where a stranger lands and cannot press play is a bug - so a tenant whose page is live
 * with a dead play button has NOT been served, and a console that stopped at "published" would
 * report them as a success. It is the operator-side spelling of "reach without listening is vanity".
 */
export type ActivationStageKey = "signedUp" | "artist" | "release" | "pitched" | "published" | "audible";

export interface OperatorTenantRow {
	id: number;
	name: string | null;
	/** The `<handle>.getheard.now` label (round 132), or null before one is assigned */
	handle: string | null;
	createdAt: string | null;
	/** Set = the tenant asked to close; the pages stay up for the grace period (round 136) */
	closedAt: string | null;
	/** A paid tier id, or the two DERIVED states a workspace row does not store */
	planId: string;
	planStatus: string | null;
	/** What this workspace contributes to MRR right now, in cents */
	mrrCents: number;
	/** Metered vendor AI cost this period, in cents */
	periodSpendCents: number;
	/** Every cent ever metered - the trial purse's basis, and the lifetime cost side of margin */
	totalSpentCents: number;
	periodCalls: number;
	topupCents: number;
	/** mrr − spend, or **null** with no revenue: an undefined margin, never a misleading 0 */
	marginCents: number | null;
	/**
	 * OURS RATHER THAN A CUSTOMER'S - the operator's own accounts, their staff's, and anything on the
	 * app's own mail domain (`config.crew.internalEmails`, 2026-08-31).
	 *
	 * ⚠ **It marks the row rather than removing it, and the difference is the whole design.** Every
	 * aggregate in this payload skips these workspaces - the funnel, MRR, AI cost, margin, active
	 * counts, retention - because counting the house as a customer makes each of those numbers a
	 * flattering lie about a business with no customers in it. But the ROW stays in the table, because
	 * Crew is also where the operator services their own account: it is where the trial gets extended
	 * and credit granted, and hiding it would take away the tool while tidying the chart.
	 */
	internal: boolean;
	/** The furthest activation stage this tenant has reached */
	stage: ActivationStageKey;
	/** Seen in the last 30 days */
	activeRecently: boolean;
	/**
	 * AN UNCLAIMED GUEST (§AM): started with "Get started" and has not connected a login yet. Like
	 * `internal`, it marks the row rather than dropping it - the table is where the operator can SEE a
	 * guest - and every aggregate here counts claimed accounts only, because a guest is a visitor who
	 * pressed a button, not a signup.
	 */
	guest: boolean;
	/** When this account started as a guest; null for a classic signup */
	guestStartedAt: string | null;
	/**
	 * When it SIGNED UP in the sense every number here counts: a classic signup at `createdAt`, a guest
	 * at its claim, and an unclaimed guest not at all (null).
	 */
	signedUpAt: string | null;
}

/** One day of the anonymous roll-up (`operator_daily`, v113) - no workspace id, by design */
export interface OperatorDailyPoint {
	/** UTC "YYYY-MM-DD" */
	day: string;
	workspaces: number;
	/** Accounts CLAIMED that day - a classic signup, or a guest claiming (§AM) */
	signups: number;
	/** Guests who pressed "Get started" that day (§AM, v156; 0 on every day before it) */
	starts: number;
	active: number;
	paid: number;
	trialing: number;
	mrrCents: number;
	aiCostCents: number;
}

export interface OperatorMetrics {
	/** false in selfhost → the console is not offered and reports nothing */
	available: boolean;
	day: string;
	/** The "YYYY-MM" AI metering bucket the spend numbers are measured in */
	period: string;
	funnel: { stage: ActivationStageKey; count: number }[];
	tenants: OperatorTenantRow[];
	totals: {
		tenants: number;
		active7: number;
		active30: number;
		paid: number;
		trialing: number;
		closed: number;
		mrrCents: number;
		periodSpendCents: number;
		marginCents: number;
	};
	retention: {
		signups30: number;
		signups7: number;
		/** Guests who pressed "Get started" in the window, claimed since or not (§AM) */
		starts30: number;
		starts7: number;
		/** Guests right now: started and not claimed, and not closed */
		guestsOpen: number;
		/** Share of tenants past their trial that are paying; null while nobody has left a trial */
		trialToPaid: number | null;
		closed30: number;
		/** 7-day actives ÷ 30-day actives - habit vs visit; null with no actives at all */
		stickiness: number | null;
	};
	history: OperatorDailyPoint[];
}

// ── The hub's custom domain (round 171) ─────────────────────────────────────

/**
 * What the domain card's checklist is drawn from - and, since round 171, whether the app has
 * actually COMMITTED to the domain.
 *
 * It lives here rather than in `services/hubSite.ts` because the settings island had hand-mirrored
 * it, which is round 150's rule: a payload the frontend re-declares by hand drifts silently, and the
 * moment to stop mirroring is the round the shape changes.
 */
export interface DomainCheck {
	domain: string | null;
	/** The nameservers the owner should have pasted at the registrar */
	nameServers: string[];
	/** We have attached the domain to the hub site on the host's side (local knowledge) */
	connected: boolean;
	/**
	 * Whether the domain has been PROVEN to serve and is therefore what published URLs are built
	 * from. Deliberately distinct from `allGood`, which is what the three checks say this second:
	 * this is what the app has committed to, and until it flips the pages are still served from
	 * `fallbackUrl`. The card says so, because a knob that has been set and is not yet in effect has
	 * to admit it.
	 */
	live: boolean;
	/** The provider URL the pages are on while `live` is false, so the card can name it */
	fallbackUrl: string | null;
	/** The domain's live NS records point at the host - i.e. the registrar change took effect */
	nameserversOk: boolean;
	/** What the NS records actually resolve to right now (for the "still your registrar" hint) */
	nameserversFound: string[];
	/** The apex answers over HTTPS with a valid certificate */
	httpsOk: boolean;
	/** All three green - the domain is fully live */
	allGood: boolean;
	/**
	 * When the domain STOPPED answering, once the app is sure enough to say so (round 178) - and
	 * `null` while there is any doubt, which is every ordinary state including "we cannot reach it
	 * this second". A card showing three red lights says "connecting" for a domain on its way up and
	 * has to say something else entirely for one on its way down, because the first is progress and
	 * the second is every published URL being dead.
	 */
	darkSince: string | null;
	/** Whether this domain may be disconnected - see `hubSite.disconnectDomain` for the three rules */
	removable: boolean;
}

/**
 * ONE ARTIST'S OWN DOMAIN (BACKLOG §P, round 254) - the hosted lane, and deliberately NOT a second
 * `DomainCheck`.
 *
 * ⚠ **The two share almost no field, which is the tell that they are two features.** `DomainCheck` is
 * about a domain DELEGATED to a vendor that also runs DNS, so its evidence is nameservers and its
 * question is "did the registrar change take effect". Here the tenant keeps their DNS wherever it is
 * and points one record at our box, so the evidence is a TXT record they created and a marker file
 * our host answers with. A single merged type would have had five fields that are null in one lane,
 * and the round after would have to guess which lane a reader is in.
 *
 * The three booleans are three different questions, in the order they can be answered:
 *
 *   - `verified` - the zone holder created `_getheard.<domain>` with `token`. Nothing is served and no
 *     certificate is issued before this, and it is what makes a claim more than a typed string.
 *   - `serving` - what the internet says THIS SECOND (the marker file answers with the artist's slug).
 *   - `live` - what the app has COMMITTED to: only then are `bio_url`, `link_url`, `epk_url` and
 *     `kit_url` built from the domain. The gap between the last two is round 171's ratchet, and the
 *     card has to admit it: a knob that has been set and is not yet in effect must say so.
 */
export interface ArtistDomainStatus {
	/**
	 * ⚠ **Whether this INSTALL offers the feature at all - a field, and deliberately not a refusal.**
	 * The first draft made `GET .../domain` answer 409 where the pages live on a vendor, and the card
	 * hid itself when the call failed. It worked, and it put `Failed to load resource: 409` in the
	 * browser console of every selfhost session - which the UI suite reads as a defect, correctly:
	 * **a capability question has to be ANSWERABLE, because an expected refusal is indistinguishable
	 * from a bug to everything downstream of it.** The WRITES still refuse; asking is not a mistake.
	 */
	offered: boolean;
	domain: string | null;
	/** The TXT record's NAME, so the card never asks the reader to assemble it themselves */
	txtName: string | null;
	/** The token to put in it - null once verified, because a proof that has been accepted is clutter */
	token: string | null;
	verified: boolean;
	live: boolean;
	serving: boolean;
	/** When the domain stopped answering, once the app is sure enough to say so (see `DomainCheck.darkSince`) */
	darkSince: string | null;
	/**
	 * The `getheard.now/a/<artist>` address that serves whatever happens here - it never stops working.
	 *
	 * ⚠ **`null` for an artist who has never published**, and that is not a missing value: the slug is
	 * minted at publish time out of an install-wide pool that burns names forever, so a card READ may
	 * not conjure one. The card says the shorter sentence instead of naming an address nobody holds.
	 */
	fallbackUrl: string | null;
	/** What to create at the registrar, resolved from this box's own hostname rather than configured */
	target: { host: string; ipv4: string[]; ipv6: string[] };
}

// ── Install health (round 144, BACKLOG A.8's performance half) ───────────────

/**
 * Every background sweep, as a closed list - see `services/sweeps.ts` for why it is closed: an
 * ABSENCE is the worst failure this app can have (a scheduler nothing calls has no symptom), and an
 * absence is only detectable against a list nothing may add itself to.
 *
 * ⚠ **The ARRAY is the declaration and the type is DERIVED** (round 187's rule, applied here in round
 * 207 because this list had been written out TWICE - the union lived here, hand-typed, and the array
 * in `services/sweeps.ts`). Two copies of one closed list is the fork this repo keeps finding: adding
 * a sweep to the array alone failed `tsc` at its `startSweep` call, which is lucky rather than
 * designed, and adding one to the union alone would have failed nothing at all - a name the canary
 * counts and no module can arm. It lives in `shared/` because that is where the type had to be, and
 * the array has to be wherever the type is derived from.
 *
 * ⚠ The order is the order they are armed in `index.ts`, which is deliberate but not load-bearing.
 */
export const SWEEP_NAMES = [
	"campaigns",
	"drip",
	"digest",
	"beacon",
	"offers",
	"social",
	"publish",
	"autopilot",
	"releaseDay",
	"trustAudit",
	"lifecycleMail",
	"retention",
	"notifyMail",
	"operatorRollup",
	"hubDomain",
	"mailAuth",
	"spotifyLink",
	"siteRollup",
	"geoRefresh",
	"example"
] as const;

export type SweepName = (typeof SWEEP_NAMES)[number];

export interface SweepHealth {
	name: SweepName;
	/** When this process armed the sweep. Nothing here survives a restart, on purpose. */
	armedAt: string;
	lastStartedAt: string | null;
	/** Null after a restart, and null forever for a sweep that has failed every tick */
	lastSuccessAt: string | null;
	lastDurationMs: number | null;
	nextRunAt: string | null;
	runs: number;
	failures: number;
	/** Reset to 0 by any success - a sweep failing *now* is a different thing from one that once did */
	consecutiveFailures: number;
	/** Ticks skipped because the previous one was still going: the "falling behind" signal */
	overlaps: number;
	lastError: string | null;
	running: boolean;
	/** Overdue by more than one interval and not currently working */
	stalled: boolean;
}

/** One route's latency, from an in-memory sample window. Nothing is stored. */
export interface RouteLatency {
	/** The route PATTERN (`/api/releases/:id`), never a real URL - see middleware/requestTiming.ts */
	route: string;
	count: number;
	p50: number;
	p95: number;
	p99: number;
	max: number;
	/** 5xx responses in the window */
	errors: number;
}

/**
 * WHETHER THE WEB INDEX IS STILL ANSWERING - the owner's ask, 2026-08-22: *"if Bing starts coming back
 * empty, we want that to appear as a health alert inside Crew."*
 *
 * ⚠ **The lane it watches is a SCRAPE, and a scrape's failure mode is silence.** It does not error, it
 * does not 403, it returns a page whose results it could not read - which is exactly what happened twice
 * in one day to the reader itself. Downstream, an empty index is indistinguishable from *this artist has
 * no Spotify presence*, and that sentence goes in front of the artist. **So the alarm cannot live at the
 * call site; it has to be a count somebody can look at.**
 *
 * ⚠ **`consecutiveEmpty` is the signal, not `empty`.** An empty answer is legitimate and common - most
 * artists this app serves are not in any index - so a raw count of empties says nothing. A RUN of them,
 * where there used to be answers, is the shape of a lane that has stopped working. Same reasoning as
 * `SweepHealth.consecutiveFailures`, and for the same reason: a lane failing *now* is a different fact
 * from one that once did.
 *
 * ⚠ **Nothing here is stored**, like every other reading in `InstallHealth`: it is a fact about the
 * process running right now, and a restart is allowed to forget it.
 */
export interface WebIndexHealth {
	/** Which provider is configured and not refused - `null` means the lane is off, which is not an alarm */
	provider: string | null;
	/** Providers this process has put down after a 403 - an entitlement refusal, never a quota one */
	refused: string[];
	asked: number;
	/** Answers that carried at least one link on the site we asked about */
	answered: number;
	/** Answers that carried none - legitimate on its own, alarming in a run */
	empty: number;
	/** Could not ask at all: a launch failure, a timeout, a refusal */
	failed: number;
	consecutiveEmpty: number;
	lastAskedAt: string | null;
	/** The last time it actually returned something - the number that dates an outage */
	lastAnsweredAt: string | null;
	lastError: string | null;
}

export interface InstallHealth {
	/** Uptime of this process, in seconds */
	uptimeSeconds: number;
	sweeps: SweepHealth[];
	/** Slowest first, capped - the lanes worth looking at, not every route */
	routes: RouteLatency[];
	/** How many requests the latency window currently holds, across all routes */
	sampled: number;
	/**
	 * How many route patterns are being sampled at all, and the ceiling on that (round 257).
	 *
	 * ⚠ **The sampler stops TRACKING at its cap, and until this field existed the health page could
	 * not say so.** This app declares several hundred route patterns and `requestTiming.ts` holds 64
	 * rings; past that, a pattern first seen later is never measured, so a route missing from the
	 * table means *not in the slowest fifteen* OR *never sampled at all* - two answers that call for
	 * opposite responses, rendered as the same absence. The cap itself is right (an evicting map over
	 * an unbounded key space just churns); what was wrong is that a diagnostic was silent about the
	 * limits of its own evidence.
	 *
	 * ⚠ **Both are OPTIONAL, and that is the seam rather than laziness.** This payload crosses an
	 * HTTP hop between two processes that are deployed together but not atomically, and
	 * `crew/appHealth.ts` casts the JSON blind. An older app answers without these keys, and a view
	 * that read `?? 0` would print "tracking 0 route patterns" - a confident, false diagnosis. Absent
	 * means *this app cannot tell me*, and the page says nothing rather than something wrong.
	 */
	trackedRoutes?: number;
	routeCapacity?: number;
	/**
	 * ⚠ **OPTIONAL for the seam's reason, not laziness** - the same one `trackedRoutes` carries: this
	 * payload crosses an HTTP hop between two processes deployed together but not atomically, and
	 * `crew/appHealth.ts` casts the JSON blind. An older app answers without this key, and a view that
	 * defaulted it would print a confident "0 asked, 0 answered" - a lane reported dead because the
	 * process answering is a version older than the question.
	 */
	webIndex?: WebIndexHealth;
	storage: {
		/** Bytes on disk under the data directory, by what they are */
		dbBytes: number;
		audioBytes: number;
		videoBytes: number;
		otherBytes: number;
		totalBytes: number;
		/** When this was measured - it is cached, because the video tree can be large */
		measuredAt: string;
	};
}

/**
 * WHICH of the reverse trial's two walls ended it (round 204, BACKLOG §N.3).
 *
 * ⚠ **A reverse trial ends TWO ways and only one of them is a date** - round 128 wrote that sentence
 * for the lifecycle mailer and it is just as true afterwards: the fourteen days can run out, or the
 * small trial purse can be spent while the calendar still has days on it. `effectivePlan()` folds
 * both into "not on trial anymore", which is right for a GATE and useless for anything that has to
 * SAY what happened - "your trial ended" beside a date three days in the future reads as a bug.
 */
export type TrialEndWall = "clock" | "purse";

/**
 * The state the app was silent about until round 204: this workspace has stopped working, and every
 * lever on it is zero.
 *
 * `null` everywhere it does not apply - selfhost, the operator's own workspace, a live trial, a paid
 * plan, and a workspace that never had a trial at all. It is not an ATTENTION row (those are
 * one-liners about work waiting); it is the one screen in this app whose job is to sell, so it
 * carries what that screen needs and nothing else.
 *
 * ⚠ **It is a DISCRIMINATED UNION, and round 205 widening it is the whole point rather than a
 * refactor.** Round 204 shipped the lapsed trial and deliberately excluded the former subscriber,
 * because "your trial has ended" is a false sentence to somebody who cancelled a paid plan on
 * purpose. That exclusion left them meeting NOTHING - a `free` workspace with every lever at zero, a
 * plan card reading "AI runs on your own API key", and Stripe's receipt as the only thing that ever
 * spoke. Two sentences were needed, and the tempting shape was a second derivation beside this one.
 * **That is exactly the fork round 204 spent a round collapsing** - three modules independently
 * answering "why has this workspace stopped" - so the second state joins the union here instead, and
 * one function still answers the question.
 */
export type PlanStoppedNotice =
	| {
			/** The reverse trial ran out - the round-204 case. */
			kind: "trial";
			wall: TrialEndWall;
			/** When the trial's clock ran (or runs) out. ⚠ On the `purse` wall this is still in the FUTURE. */
			endsAt: string;
			canSubscribe: boolean;
	  }
	| {
			/**
			 * A subscription was cancelled and has now lapsed (`plan_status = 'canceled'`).
			 *
			 * ⚠ **It carries no date, and inventing one would be this app's own worst habit.**
			 * `applySubscriptionDeleted` nulls `plan_renews_at`, so nothing in the database knows when
			 * the plan actually ended - and a sentence naming a day we guessed, to the one reader who
			 * knows exactly when they cancelled, is worse than a sentence with no day in it.
			 */
			kind: "canceled";
			canSubscribe: boolean;
	  };

/**
 * What a stopped workspace STILL HAS - the counts the sell screen puts beside "nothing was deleted"
 * (round 284).
 *
 * ⚠ **It exists because the promise was a sentence and a sentence is cheap.** `trialEnd.kept` has
 * always said *nothing was deleted and nothing was taken down*, which is true, and a reader deciding
 * whether to pay has no reason to believe it - it is exactly what an app would say either way. Their
 * own numbers are the same claim in a form that cannot be bluffed, and they are the strongest honest
 * argument this screen has: the work is already done and it is waiting.
 *
 * ⚠ **A ZERO IS NEVER RENDERED, and that is a design rule rather than a formatting one.** A tenant
 * who lapsed having made almost nothing would be shown *0 pitches, 0 pages* on the one screen whose
 * job is the sale - an inventory of what they failed to do, arguing the opposite case at the moment
 * of the decision. Every field here is a count the panel may DROP, and the panel falls back to the
 * plain sentence when they are all zero. **A number that can be zero is only usable where the zero
 * is also worth saying.**
 *
 * ⚠ **These are counted from the tenant's own rows, never from the plan.** Nothing here is
 * capability-shaped, so no entry drifts when `PLAN_CAPABILITIES` moves.
 */
export interface PlanStoppedKept {
	/** Releases in the workspace, whatever state they are in */
	releases: number;
	/**
	 * Pitches SENT, all time, follow-ups included.
	 *
	 * ⚠ **Deliberately a different basis from the pitch CAP's count** (`OUTREACH_COUNTS_SQL` excludes
	 * a follow-up, because a cap is about reaching new strangers and a follow-up reaches nobody new).
	 * The question here is not what was spent against an allowance, it is how much work is sitting in
	 * this workspace - and a follow-up the owner wrote and sent is that.
	 */
	pitches: number;
	/** Replies that came back, and go on coming back - reading them never stops at an expiry */
	replies: number;
	/** Public pages still being served: the smart links, press kits, fan kits and bio pages */
	pages: number;
	/**
	 * Fans who asked to hear when a release lands and have NOT been told yet - people, on the other
	 * end, still waiting.
	 *
	 * ⚠ **Not every signup**: a fan who has had their release-day mail is no longer waiting, and one
	 * the retention sweep has anonymised is a measurement rather than a person. See the query - the
	 * chip beside this number says *waiting*, so the count has to mean it.
	 */
	fans: number;
}

/**
 * What plan a workspace is EFFECTIVELY on - derived, never stored (`services/billing.ts` computes it
 * from the workspace row plus two clocks, and its header is where the WHY lives).
 */
export interface EffectivePlanInfo {
	/** "operator" = workspace 1 in saas: the platform's own workspace, uncapped by definition */
	id: PlanId | "operator";
	name: string;
	/** A live paid subscription is behind this plan */
	paid: boolean;
	/** Currently inside the no-card reverse trial (full Pro features on a small budget) */
	trial: boolean;
	trialEndsAt: string | null;
	/** null = uncapped */
	artistLimit: number | null;
	/**
	 * How many pitches this workspace may send; null = uncapped.
	 *
	 * ⚠ **It is NOT "per month" any more, which is why it stopped being called that** (§AB round 1). A
	 * paid plan's number is monthly; the trial's is total for the fortnight, and a field named
	 * `outreachPerMonth` holding a trial total is a question answered in a field that was answering a
	 * different one. `outreachBasis` says which it is, and no reader may assume.
	 */
	outreachLimit: number | null;
	/** What `outreachLimit` is measured against: the current month, or (trial) every pitch since signup */
	outreachBasis: "period" | "total";
	/** May arm the release autopilot */
	autopilot: boolean;
	/** May run ad campaigns from inside the app */
	adCampaigns: boolean;
	/**
	 * The §AD entitlement levers - what an expiry stops (`shared/plans.ts`'s `PLAN_CAPABILITIES`).
	 *
	 * ⚠ **They are on the EFFECTIVE plan and not read off `PLANS[id]` by their callers**, for the
	 * reason the outreach pair one field up exists: the trial derives Pro's levers, the operator
	 * derives none of them, and a gate that resolved its answer through the plan table would refuse
	 * an operator and wave a lapsed trial through - both silently, and both only in saas.
	 */
	reels: boolean;
	/** The bio page and the smart link - the half of publishing a FREE workspace keeps (§AK) */
	publishing: boolean;
	/** The press kit and the fan kit - the paid half of publishing (§AK) */
	kits: boolean;
	posting: boolean;
	mentions: boolean;
	/**
	 * How many releases this workspace may HOLD; `null` = no ceiling (round 284, owner 2026-08-28).
	 *
	 * ⚠ **A CAP, not a capability, and the shape says why**: creating the FIRST release is how a lapsed
	 * tenant reaches every wall this app has - all of them live inside a release - so the honest answer
	 * is "yes, once", which is a number. See `PlanDef.releaseLimit` for the full argument, and
	 * `tests/planMatrix.test.ts` for the gate that keeps it out of `PLAN_CAPABILITIES`.
	 */
	releaseLimit: number | null;
	/** Included AI budget in cents; null = unmetered (operator). During the trial this is the trial budget. */
	aiBudgetCents: number | null;
	/**
	 * What AI spend the budget is measured against: the current month, or (trial) everything since
	 * signup. ⚠ It was `budgetBasis` until §AB gave outreach the same axis - one generic name over two
	 * bases is the ambiguity the pair exists to remove.
	 */
	aiBudgetBasis: "period" | "total";
	/**
	 * Has this workspace been CLAIMED (§AM)? False only for a guest that started with "Get started" and
	 * has not connected a login yet - true for the operator, every selfhost install and every classic
	 * signup. It rides on the effective plan because every wall already reads this one derivation, so a
	 * "claim first" wall and a "pick a plan" wall can never disagree about the same workspace.
	 */
	claimed: boolean;
}

/** What a surface needs to say "you have N pitches left" before the Send button is pressed. */
export interface PlanQuota {
	/** Pitches that have counted against `limit` - this month's, or the trial's whole run (see `basis`) */
	used: number;
	/** `null` = unlimited (every selfhost install, the operator, and the plans with no cap) */
	limit: number | null;
	/** `null` when there is no limit; never negative */
	remaining: number | null;
	/**
	 * WHICH sentence the surface may say (§AB round 1).
	 *
	 * ⚠ **A count with no basis cannot be phrased.** "3 pitches left this month" is the truth on a paid
	 * plan and a lie during the trial, where the number does not come back on the 1st - and the trial is
	 * the reader most likely to see the wall, because ten is a small number. So the basis travels with
	 * the count rather than being inferred from `plan.trial` by each surface separately.
	 */
	basis: "period" | "total";
}

/**
 * The outreach lane's reading, and the name every existing caller knows it by.
 *
 * ⚠ **The shape was generalized in round 284 and the name was kept**, because a quota is a quota: the
 * pitch cap and the AI allowance answer the same four questions and the meter that draws them must
 * not care which lane it is looking at. Renaming twenty call sites would have said nothing the alias
 * does not.
 */
export type OutreachQuota = PlanQuota;

/**
 * The snapshot `GET /api/billing` returns: the Settings plan card's own read, plus the two walls
 * round 190 put here and the trial-ended state round 204 did.
 *
 * ⚠ **These three moved here from `services/billing.ts` in round 204, and the trigger is the rule
 * this repo already had rather than tidiness:** `pages/settings.ts` held a HAND-MIRRORED copy of this
 * interface, and the moment to stop mirroring a payload is the moment its shape changes (round 195's
 * finding, round 203's addendum about the envelope). The mirror was already two fields stale - it
 * never grew round 190's `adsAllowed`/`outreach` - which is the failure exactly: both sides typecheck
 * green while the page renders `undefined` for whatever the copy has not heard about.
 */
export interface BillingState {
	/** Stripe is configured on this server - checkout/portal/top-up buttons work */
	configured: boolean;
	operator: boolean;
	plan: EffectivePlanInfo;
	trialDaysLeft: number | null;
	subscription: { status: string; renewsAt: string | null; cancelAtPeriodEnd: boolean } | null;
	artistCount: number;
	/**
	 * Releases in this workspace - the other half of `plan.releaseLimit`, so the toolbar can wall the
	 * Add buttons before the click rather than after a 402 (round 190's "every knob is a promise").
	 *
	 * ⚠ **It rides this payload rather than being counted in the page from the rows it already has**,
	 * unlike the artists page, which does exactly that. The Releases page paginates and filters, so
	 * `this.releases.length` is a count of what is ON SCREEN - which would open the gate the moment
	 * somebody typed in the search box.
	 */
	releaseCount: number;
	/**
	 * This workspace has a Stripe customer, so there is a billing history to reach - invoices, the
	 * card on file, and the cancel button.
	 *
	 * ⚠ **It is NOT `plan.paid`, and conflating the two hid every invoice a former subscriber ever
	 * paid** (owner question, 2026-08-10: *"how does a user actually cancel their plan or trial? And
	 * how do they go to Stripe checkout or whatever to see their invoices?"*). The Manage billing
	 * button was drawn only while a subscription was LIVE, so the moment somebody cancelled - the one
	 * moment they are most likely to come looking for a receipt - the only door to the Stripe portal
	 * disappeared from the app. `POST /api/billing/portal` always worked; nothing rendered the button.
	 * A trial is the other half of the same question and needs no button at all: there is no card, so
	 * there is nothing to cancel, and the card SAYS that instead of leaving them hunting.
	 */
	hasBilled: boolean;
	topupCents: number;
	/**
	 * ⚠ Round 190 (BACKLOG §N.4). These two ride on the snapshot the Settings plan card already
	 * fetches rather than on two new endpoints, because their READERS are Compose and the boost
	 * composer - surfaces that must know the answer before they paint a button, and a wall that
	 * arrives on its own request is a wall the button renders without.
	 */
	adsAllowed: boolean;
	outreach: OutreachQuota;
	/**
	 * ⚠ Round 204. The plan card's summary said "Free plan: AI runs on your own API key" to somebody
	 * whose trial ended this morning - true, and not the sentence they came to Settings to read. The
	 * SELL screen is the dashboard's (one place, written once); this is the status line beside it, and
	 * it is here so the two cannot disagree about whether a trial ended or which wall ended it.
	 */
	planStopped: PlanStoppedNotice | null;
	/**
	 * The AI budget reading, composed onto this response by `routes/billing.ts` (round 284).
	 *
	 * ⚠ **Optional because `billingState()` does not build it, and that is a MODULE-GRAPH fact rather
	 * than an oversight**: the allowance lives in `services/aiUsage.ts`, which already imports
	 * `effectivePlan` from `services/billing.ts` - so having billing import the allowance back would
	 * put a cycle in the one lane where a half-initialized module is a wrong CHARGE. The route sees
	 * both and composes there.
	 *
	 * ⚠ **It is here so a surface that already fetches `/api/billing` can draw an AI meter without a
	 * second request** - round 190's rule, which is the same reason `outreach` and `adsAllowed` are on
	 * this payload: a reading that arrives on its own request is a reading the page paints without.
	 */
	usage?: UsageSummary;
	/**
	 * What this workspace still HAS, counted - null unless `planStopped` is non-null (round 284).
	 *
	 * ⚠ **Null is "not asked", never "nothing kept".** It is five COUNT queries, so it is only run for
	 * a workspace that has actually stopped; a live trial and a paying subscriber get null here and it
	 * says nothing about their data. A reader that treated null as zero would print an inventory of
	 * emptiness on the one screen that must not.
	 */
	planKept: PlanStoppedKept | null;
	/**
	 * §AD's win-back reward as a SENTENCE the plan chooser renders (owner, 2026-08-26). Null when the
	 * workspace is not owed one, when no coupon is configured, or when the coupon is a shape the app
	 * declines to describe.
	 *
	 * ⚠ **Null here does NOT mean no discount.** The money is attached to the Checkout Session by
	 * `winBackDiscount()` on evidence this field does not carry, and Stripe's own checkout page shows
	 * it either way - so a reader must never infer "full price" from an absent sentence. The two are
	 * kept apart on purpose: a number this app states about a charge has to be one Stripe holds, and
	 * declining to describe an unusual coupon is free where approximating it is not.
	 *
	 * ⚠ **A `KeyText`, never prose.** A server payload naming app chrome is one of the three blind
	 * spots no i18n scanner can see (`docs/notes/text-and-locale.md`), and the type is the fix.
	 */
	winBack: KeyText | null;
	/**
	 * The code this workspace types into Stripe's promotion-code box, once it has been minted.
	 *
	 * ⚠ **Null means "not minted yet", NOT "no offer"** - the two are different states and only
	 * `winBack` answers the second. Minting is a WRITE (it creates a promotion code at Stripe), so it
	 * cannot happen inside this synchronous read; the surface that shows the offer asks
	 * `POST /api/billing/winback-code` for it, which is idempotent.
	 */
	winBackCode: string | null;
}

/**
 * The AI budget/spend snapshot `GET /api/usage` returns and the Settings card renders.
 *
 * ⚠ It lives HERE rather than in `services/aiUsage.ts` because the frontend had re-declared it by
 * hand, which is the drift round 120 named and round 150 proved bites in silence: a hand-mirrored
 * server interface typechecks green on BOTH sides while the page renders `undefined`. Round 164
 * changed this payload's shape, which is exactly the moment the rule says to stop mirroring.
 */
export interface UsageSummary {
	/** false in selfhost → the UI hides the whole surface */
	metered: boolean;
	/* ⚠ `provider` lived here from round 164 until 2026-08-09 and went out with BYOK. It existed
	   because a tenant's own key could CHANGE which lane their calls ran on, which nothing on the
	   card could otherwise reveal; without BYOK every tenant runs on the operator's one install lane,
	   which is neither theirs to change nor theirs to act on. Removing it rather than leaving it
	   unread is §AB round 2's finding applied to ourselves: **a payload field no surface reads is the
	   worst case in the family, because deleting it breaks nothing and nobody notices it is a leak of
	   how the operator's install is configured.** */
	/** no cap applies here: the operator's own workspace, or an explicit 0 override */
	unmetered: boolean;
	period: string;
	/** the included base budget (plan-derived, or the operator override) */
	budgetCents: number;
	/** spend on the budget's basis (this month; total-since-signup during the trial) */
	spentCents: number;
	/** what's left including any operator-granted credit */
	remainingCents: number;
	/**
	 * Unconsumed AI credit, drawn down only after the included budget.
	 *
	 * ⚠ It used to be PURCHASED (the AI top-up packs, removed 2026-08-09 - `shared/plans.ts` has the
	 * why). The only thing that can put credit here now is an operator grant from Crew, which is a
	 * support gesture rather than a SKU - so a non-zero value is rare and always means somebody
	 * decided this tenant was owed something.
	 */
	topupCents: number;
	calls: number;
	/** true when the whole allowance is gone and AI is currently walled */
	exhausted: boolean;
	resetsAt: string;
	plan: { id: string; name: string; paid: boolean; trial: boolean; trialEndsAt: string | null };
}

/**
 * What `GET /api/usage` answers with.
 *
 * ⚠ It used to carry `byokKeys` and `verified` too, for the tenant's own pasted API key. **BYOK was
 * removed on 2026-08-09** (see `resolveAIProvider`), and the alias is kept rather than collapsed
 * because the route, the page and three tests name this type - a rename would be churn across four
 * files to say the same thing. It is the summary, and today nothing is added to it.
 */
export type UsageResponse = UsageSummary;

/**
 * What the quick start derived about a release before it exists - `GET /api/releases/quickstart/inspect`.
 *
 * ⚠ **It lives HERE rather than in `services/quickstart.ts` because both sides read it**, and the
 * frontend used to hand-mirror it. Round 195 grew the payload by ten fields and that is exactly the
 * trigger `src/frontend/CLAUDE.md` names: *a hand-mirrored server interface typechecks green on both
 * sides while rendering `undefined`*, so a page mirroring a payload should import it the moment the
 * payload changes shape. Two copies would have gone green with the wizard reading nothing.
 */
export interface QuickstartDerived {
	title: string | null;
	artist_name: string | null;
	album_title: string | null;
	release_type: "single" | "ep" | "album" | null;
	release_date: string | null;
	recording_year: number | null;
	genre: string | null;
	label: string | null;
	duration: string | null;
	cover_image: string | null;
	spotify_album_id: string | null;
	spotify_url: string | null;
	/**
	 * Which catalog record this release was picked out of (§V round 2, round 203) - the pair that
	 * survives a second vendor, where `spotify_album_id` above cannot.
	 *
	 * ⚠ **It is not a duplicate of `spotify_album_id` even when it holds the same string.** That field
	 * is what the release-day watcher polls and what becomes a Listen button; this one answers "have I
	 * already set this record up", which is a question about a CATALOG and has to be askable of Deezer.
	 * A Spotify pick fills both, and they mean different things on the day they agree.
	 */
	catalog_vendor: CatalogPickService | null;
	catalog_album_id: string | null;
	/**
	 * The credits the file's own ID3 tag carries (§S, round 195). ⚠ These come from ONE source and
	 * always will: `services/audioMetadata.ts`. Spotify has no `label` on the album object any more,
	 * `/audio-features` 403s for every app created after Nov 2024, and there has never been a lyrics
	 * endpoint - so the artist's own MP3 is not merely the cheapest source for these, it is the only
	 * one this app has with no matching problem attached.
	 */
	isrc: string | null;
	ean_upc: string | null;
	bpm: number | null;
	composed_by: string | null;
	producer: string | null;
	recorded_by: string | null;
	mixed_by: string | null;
	mastered_by: string | null;
	recording_studio: string | null;
	lyrics: string | null;
	/**
	 * Which source filled each of the above (§S, round 196), so the wizard's one-line note can name
	 * it. ⚠ **It is a LIST rather than a flag** because the two sources compose per field: the tag
	 * carries the ISRC and the BPM, MusicBrainz carries the label and the producer, and a single
	 * "matched a catalog" boolean could not tell the owner which half came from where.
	 */
	prefill: PrefillMark[];
}

/**
 * Everything the quick start could derive about a release before it exists.
 *
 * ⚠ **It followed `QuickstartDerived` here in round 203, for the identical reason and on the identical
 * trigger.** The frontend mirrored this one too, and `sources` grew a third arm this round - the exact
 * shape change `src/frontend/CLAUDE.md` names: a mirror that falls behind typechecks green on both
 * sides while the page reads `undefined`. Round 195 moved the field list and left the envelope behind,
 * which is how a mirror survives the round that was supposed to end it. **When you retire one, look at
 * what wraps it.**
 */
export interface QuickstartInspection {
	derived: QuickstartDerived;
	/** Existing artist whose name matches the derived artist_name - the create step attaches instead of duplicating */
	artist_match: { id: number; name: string } | null;
	/**
	 * ⚠ **`catalog` is a THIRD arm rather than a widening of `spotify`** (round 203). These decide what
	 * the wizard may say about where the values came from, and "a catalog record the owner pointed at"
	 * is not the same claim as "a Spotify link they pasted" - collapsing them would make the one honest
	 * sentence about the strongest source this lane has read like the weakest.
	 */
	sources: { audio: boolean; spotify: boolean; catalog: boolean };
	warnings: KeyText[];
}

/**
 * What the post-create fill-in can go looking for, as a closed list - `services/releaseEnrichment.ts`
 * is the registry and the release workspace is what renders it.
 *
 * ⚠ **The wire carries the KEY, never the words.** These are two owner-facing names ("the Spotify
 * link", "the lyrics") produced by a server that does not know which of the three dictionaries this
 * reader is in - the exact class-2 blind spot `docs/notes/text-and-locale.md` names, where a payload
 * FIELD is a call site no scanner can see. The union crosses; the page says it in the owner's own
 * language.
 *
 * ⚠ **Closed, for the reason every list in this file is**: the strip has to render a NAME for each
 * one, and a free string would render as itself in whichever language the server was written in.
 */
export const RELEASE_ENRICHMENT_FIELDS = ["spotify", "lyrics"] as const;

export type ReleaseEnrichmentField = (typeof RELEASE_ENRICHMENT_FIELDS)[number];

/**
 * What the app is doing to this release right now, or the idle answer.
 *
 * ⚠ **`looking` is what the task actually ATTEMPTED, and `filled` is what reached a column** - not
 * what a vendor answered. Every write in that task is blanks-only and re-checked at the statement, so
 * a lookup can succeed against a field the owner has meanwhile typed into, and announcing that as
 * filled would be the app claiming credit for a value it correctly refused to write.
 *
 * ⚠ **`startedAt: null` is the whole of "nothing to say".** A release created before this process
 * booted and a release nothing is filling in are the same answer to the owner, and the client must
 * not have to tell them apart.
 */
export interface ReleaseEnrichmentStatus {
	running: boolean;
	startedAt: string | null;
	finishedAt: string | null;
	looking: ReleaseEnrichmentField[];
	filled: ReleaseEnrichmentField[];
}

/**
 * The services that can enumerate ONE artist's releases - §V round 2 (round 203), Apple added in
 * round 227.
 *
 * ⚠ **This is a shorter list than `CATALOG_VENDORS` and the difference is the whole point.** That
 * union is who can be asked *about one exact key* - a barcode, an ISRC - and MusicBrainz answers
 * there and cannot appear here, being the one source round 200 measured as blind to a brand-new
 * artist. This one is who can be asked *what has this person put out*, which needs an artist the app
 * can name in that vendor's own ids. **Two lists because they answer two questions.**
 *
 * ⚠ **This list excluded Apple for four rounds on a reason that our OWN app had already made false,
 * and that is the part to carry forward.** It used to read *"Apple has an artist endpoint and no
 * keyless way to reach an id for it from a link the owner already stored"* - true when written, and
 * falsified three rounds later by round 199 shipping the Apple **Find** button, which stores
 * `music.apple.com/us/artist/<slug>/<id>` with the id right there in the path. Round 200 wrote the
 * vendor-shaped version of this rule (*a "no" about a vendor is a measurement of ONE field, and it
 * expires when somebody looks at a second one*); this is the other direction, and it is easier to
 * miss: **a refusal can expire because the app changed what it holds, with nothing about the vendor
 * moving at all.** Re-derive it when a sibling round changes the inputs.
 *
 * ⚠ **The ORDER is what the rest of the app can DO with the answer, not how good the catalog is.** A
 * Spotify album id is what `releaseDayWatch` polls, `socialIngest` snapshots against and
 * `releaseLinks` renders as a Listen button; a Deezer id and an Apple id are catalog keys and
 * nothing else. Between the two keyless ones Deezer goes first for one measured reason: it answers
 * `record_type` (`single`/`ep`/`album`) where **Apple's `collectionType` is the literal string
 * `"Album"` for every record it has, singles and EPs included** - Apple's only type signal is the
 * `- Single` / `- EP` suffix it appends to the NAME, which `appleCollectionTitle` recovers. A vendor
 * that answers the question directly outranks one whose answer has to be reconstructed.
 */
export const CATALOG_SOURCE_SERVICES = ["spotify", "deezer", "itunes"] as const;

export type CatalogSourceService = (typeof CATALOG_SOURCE_SERVICES)[number];

/**
 * What to call each of them on screen.
 *
 * ⚠ **These do NOT go through `t()` and never will** - they are proper nouns, the rule `shared/brand.ts`
 * states about this app's own name applied to somebody else's. A Danish UI naming a fourth streaming
 * service is talking about a service that does not exist. i18n-ignore: brand names.
 *
 * ⚠ **A map keyed by the union, not a second list**, so a third service cannot be added to
 * `CATALOG_SOURCE_SERVICES` without the compiler asking what it is called.
 */
/**
 * The services a RELEASE can be picked out of - §V, the Release omnibox (round 215).
 *
 * ⚠ **A THIRD list beside `CATALOG_SOURCE_SERVICES` and `CATALOG_VENDORS`, and it stays a third list
 * even now that it holds the same three names.** They answer three different questions:
 * `CATALOG_VENDORS` is *who can be asked about one exact key* (MusicBrainz is in that one and can
 * never be in these), `CATALOG_SOURCE_SERVICES` is *who can be asked what this artist has put out*,
 * and this is *who can be asked about one album a person pointed at*. Round 227 made two of the three
 * coincide by giving Apple the artist half; **that is a fact about today's vendors, not a merge.** The
 * next vendor that answers one question and not the other separates them again, and a collapse now
 * would have to be undone by whoever adds it.
 *
 * ⚠ **The ORDER is the preference the owner set** - *on duplicates prefer Spotify* - and it is also
 * the order the rest of the app can act on: a Spotify album id is what `releaseDayWatch` polls,
 * `socialIngest` snapshots against and `releaseLinks` renders as a Listen button, while the other two
 * are catalog keys and nothing else. `services/releaseSearch.ts` reads this array as its ladder rather
 * than restating it.
 *
 * ⚠ **It is a SUPERSET of `CATALOG_SOURCE_SERVICES`, spelled as one so it cannot drift**: a service
 * that can enumerate an artist's shelf can certainly be asked about one album off it.
 */
export const CATALOG_PICK_SERVICES = [...CATALOG_SOURCE_SERVICES] as const;

export type CatalogPickService = (typeof CATALOG_PICK_SERVICES)[number];

/**
 * What to call each of them on screen.
 *
 * ⚠ **These do NOT go through `t()` and never will** - they are proper nouns, the rule `shared/brand.ts`
 * states about this app's own name applied to somebody else's. A Danish UI naming a fourth streaming
 * service is talking about a service that does not exist. i18n-ignore: brand names.
 *
 * ⚠ **A map keyed by the union, not a second list**, so a fourth service cannot be added to
 * `CATALOG_PICK_SERVICES` without the compiler asking what it is called. It is keyed by the WIDER
 * union so there is one place these names live - a `CatalogSourceService` indexes it unchanged.
 */
export const CATALOG_SOURCE_LABELS: Record<CatalogPickService, string> = {
	spotify: "Spotify",
	deezer: "Deezer",
	// ⚠ "Apple Music", not "iTunes": `itunes` is the API's own name for the endpoint and `CatalogVendor`'s
	// spelling for the lane, and neither is what the store has been called for years. The id is a wire
	// value; this is what a person reads.
	itunes: "Apple Music"
};

/**
 * One record a catalog offers for a typed title - `GET /api/releases/quickstart/search`.
 *
 * ⚠ **Everything here fills a PICKER and nothing here fills a COLUMN.** A name search for a release is
 * refused in the prefill lane because nobody can check the answer; it is allowed here because the owner
 * reads the list and clicks their own song. The moment they do, the id goes back and the record is
 * re-read BY KEY - so a title or a date on this object is something to recognize a sleeve by, never a
 * value that lands in the database. `services/releaseSearch.ts` has the long form.
 */
export interface ReleaseSearchHit {
	/** The vendor whose id this row travels under. Spotify's where Spotify has it - see the union above */
	vendor: CatalogPickService;
	albumId: string;
	name: string;
	/** ⚠ The thing that tells four records with one title apart. A picker without it is unpickable */
	artistName: string | null;
	/** A STRING of unknown precision, with the field beside it saying which - `CatalogRelease`'s rule */
	releaseDate: string | null;
	releaseDatePrecision: "year" | "month" | "day" | null;
	albumType: string | null;
	totalTracks: number | null;
	imageUrl: string | null;
	url: string;
	/**
	 * The OTHER services that answered for this same record.
	 *
	 * ⚠ **It is what makes the merge visible, and a merge nobody can see is a list that silently
	 * dropped rows** - round 197's rule, in a picker. It also tells the owner something true and useful
	 * before they click: a record all three catalogs carry is one whose prefill will have more to work
	 * with, because `catalogLookupForKeys` will have three vendors to ask.
	 */
	alsoOn?: CatalogPickService[];
}

/** The omnibox's answer: what was found, and which catalogs were in a position to look */
export interface ReleaseSearchResult {
	hits: ReleaseSearchHit[];
	/**
	 * ⚠ **Which services this install can search, NOT which ones had a hit.** An empty result means
	 * something different depending on this: "no such record" where three catalogs looked, and "this
	 * install has no catalog credentials" where none did. Round 199's finding - a feature dark on most
	 * installs is indistinguishable from one that does not exist.
	 */
	services: CatalogPickService[];
}

/**
 * One release as a catalog offers it, in neither vendor's spelling - `GET /api/releases/quickstart/catalog`.
 *
 * ⚠ **`releaseDate` is a STRING of unknown precision and the field beside it says which.** Spotify
 * genuinely stores `"2026"` and `"2026-09"`; Deezer, measured 2026-08-06, answers a full ISO day for
 * every album the owner has out. Collapsing the two into "a date" is how a year becomes January 1st -
 * so the precision travels, and the renderer shows the year it can always trust.
 */
export interface CatalogRelease {
	/** The vendor's own id for this record - meaningless without `vendor`, which is why they travel together */
	albumId: string;
	vendor: CatalogSourceService;
	name: string;
	url: string;
	releaseDate: string | null;
	releaseDatePrecision: "year" | "month" | "day" | null;
	albumType: string | null;
	totalTracks: number | null;
	imageUrl: string | null;
	/** A release row already holds this catalog record - offer it as "already set up", never as new work */
	alreadyAdded: boolean;
}

/**
 * Why an artist has no release picker, when they have none.
 *
 * ⚠ **Three reasons rather than a `null`, because the owner can act on two of them and not on the
 * third.** "You have not put a profile link on this artist" is a thing to go and fix; "this install
 * has no Spotify credentials" is a different thing to go and fix; and "the vendor knows this artist
 * and lists nothing" is the app's most common correct answer, since an unreleased song is in no
 * catalog. Round 197's rule, one lane over: a derived choice nobody can see is indistinguishable
 * from the app ignoring what the owner just did.
 */
export const CATALOG_SOURCE_ABSENCES = ["noProfile", "vendorOff", "empty"] as const;

export type CatalogSourceAbsence = (typeof CATALOG_SOURCE_ABSENCES)[number];

/** One of the workspace's artists and the catalog the app can read on their behalf */
export interface QuickstartCatalogArtist {
	artistId: number;
	name: string;
	/** The service these releases came from, so the picker can SAY it. Null when `absence` explains why not */
	source: CatalogSourceService | null;
	absence: CatalogSourceAbsence | null;
	releases: CatalogRelease[];
}

export interface QuickstartCatalog {
	artists: QuickstartCatalogArtist[];
}

/**
 * The release columns the artist's own uploaded file can fill from its ID3 tag (§S, round 195),
 * spelled the way the `releases` row spells them.
 *
 * ⚠ **One list, here, because THREE places need it and two of them are on the other side of the
 * wire**: `services/audioMetadata.ts` decides what goes in it, the release editor maps each name
 * onto a form field, and the quick-start wizard counts them. A second copy anywhere is the fork
 * this file exists to prevent - a field added to the reader and not to the editor's map is a
 * suggestion the server offers and the UI silently drops, with every gate green.
 *
 * ⚠ `title`, `artist_name` and the cover are deliberately NOT here. The title has a filename
 * fallback with a warning attached; the artist name is what the quick start MATCHES an existing
 * artist on, so it is an input to a decision rather than a field; and the cover is hundreds of
 * kilobytes of base64 that would ride on every upload response for a field that has its own picker.
 */
export const TAG_FIELDS = [
	"album_title",
	"genre",
	"release_date",
	"recording_year",
	"label",
	"isrc",
	"ean_upc",
	"bpm",
	"composed_by",
	"producer",
	"recorded_by",
	"mixed_by",
	"mastered_by",
	"recording_studio",
	"lyrics"
] as const;

export type TagField = (typeof TAG_FIELDS)[number];

/** One field the file can fill. An ARRAY of these, so `TAG_FIELDS` order is the reading order */
export interface TagSuggestion {
	field: TagField;
	value: string | number;
}

/**
 * The services an artist's own profile can be looked up on by NAME - §V, round 199.
 *
 * ⚠ **The order is the order the artist editor renders them in**, and the four are reached by TWO
 * different mechanisms, which is the thing to know before touching this. Spotify and Apple are name
 * searches against a vendor's index. **SoundCloud and TikTok have no keyless search at all and are
 * reached by guessing the handles a name takes and asking each service which of them exist** (see
 * `services/handleProfiles.ts`, and the profile that proves every guess must be verified) - so those
 * candidates are inference plus proof, where the other two are a vendor's own answer.
 *
 * ⚠ **Instagram, Facebook and YouTube cannot be SEARCHED at all** - each for its own measured reason (the
 * table in `handleProfiles.ts`): Meta answers `200` for a handle that does not exist, so a verification
 * built on it would confirm every guess (**a check that cannot fail is not a check**), and YouTube's check
 * works but proves only that a handle is TAKEN, with the display name 700 KB into the page (**existence is
 * not identity**).
 *
 * ⚠ **They are in this list anyway, because a THIRD mechanism beats both of the others: the app already
 * knows.** The owner's point, 2026-08-06: *"the artist profile may already fill out these links on any of
 * the social profiles we're tracking already."* A connected Instagram has stored its `username` since the
 * connector was written, a connected TikTok its handle, YouTube its channel id, Facebook its page - so for
 * every platform the artist has connected, the link is a FACT the app holds rather than something to look
 * up. **Nothing to guess, nothing to verify, and no request at all.** See `connectedProfileLinks`.
 *
 * ⚠ **And round 200 added a FOURTH, which is the one that reaches all seven: MusicBrainz lists an artist's
 * own official links** (`services/artistLinks.ts`). So "cannot be searched" above is now "cannot be searched
 * ON THAT SERVICE" - the app asks a catalog which links the artist declared instead of asking the platform
 * to confirm a guess. It is the honest answer for the three the paragraph above calls impossible, and it
 * SUPERSEDES a guess where both can speak: see `ProfileSource`.
 *
 * ⚠ **Round 201 added an EIGHTH, and it is the first one added because the app was already being
 * handed the link and dropping it.** MusicBrainz's `url-rels` list a Deezer page for plenty of
 * artists and `profileUrlService` had no box to put it in, so it fell on the floor; Deezer is also
 * already in `SMART_LINK_PLATFORMS`, so the app has been sending fans there for a release while having
 * nowhere to record the artist's own page. And it is the only service in this list whose index both
 * finds an upcoming artist by name AND carries their photograph.
 */
export const PROFILE_SERVICES = ["instagram", "tiktok", "facebook", "youtube", "spotify", "apple_music", "soundcloud", "deezer"] as const;

export type ProfileService = (typeof PROFILE_SERVICES)[number];

/**
 * Why a service cannot be searched - a KEY, never a sentence (the reader's locale decides the words).
 *
 * ⚠ **`noSearchApi` was here for one round and is gone**: it was SoundCloud's, on the belief that no
 * keyless search means no lookup, and the owner refused that answer. A blocker nothing can produce is
 * dead data, so it left with the belief.
 *
 * ⚠ **`connectFirst` is a wall that is also an INSTRUCTION**, and it is the shape to copy: Instagram,
 * Facebook and YouTube cannot be searched by anybody, and the app can still fill them the moment the
 * artist connects the account. So the sentence names the thing to do rather than the thing that is
 * missing - a dead end and an unfinished setup look identical on screen and are not the same problem.
 */
export type ProfileSearchBlocker = "needsKeys" | "vendorOff" | "connectFirst";

/**
 * What the app can do for one service on this install: search it, fill it from a connection, or neither.
 *
 * ⚠ **Two capabilities, not one, because they are independent** - Instagram can be filled and never
 * searched, Apple can be searched and never connected, TikTok can be both. A single boolean would have
 * had to pick which of those it meant.
 */
export interface ProfileSearchStatus {
	service: ProfileService;
	searchable: boolean;
	/** Meaningful only when nothing can be offered - the wall the UI names on the disabled control */
	reason: ProfileSearchBlocker;
	/**
	 * The link the app ALREADY holds, from an account this artist has connected. ⚠ It is a
	 * `ProfileCandidate` like any other because the owner still clicks it: the app knowing something is not
	 * the same as the owner having said "put it on my press kit".
	 */
	connected: ProfileCandidate | null;
}

/**
 * The answer to one profile search: the rows to offer, and who the catalog matched.
 *
 * ⚠ **`matchedArtists` exists because an EMPTY list is not one fact, and the owner caught the app saying
 * the wrong one within an hour of round 200 shipping.** Searching "Wessberg" matched three real people in
 * MusicBrainz - Lis, Samuel and Erik Axel - none of whom has any of the app's seven profiles listed
 * (a Discogs page and a homepage between them), and the panel answered *"No artists came back under that
 * name. Try the spelling the service uses."* **That is false and it sends the reader to re-type a name
 * that was found.** The true sentence is the actionable one: they are in the catalog, this link is simply
 * not listed there. Round 189's absence-versus-outage split, one arm deeper.
 */
export interface ProfileSearchResult {
	candidates: ProfileCandidate[];
	/** Catalog artists this name matched, whether or not any of them carried a link for the asked service */
	matchedArtists: string[];
}

/**
 * Where a candidate came from, which is a fact about **how much the app can vouch for it** - round 200.
 *
 * ⚠ **The four are not four spellings of "we found it", and the measurement that forced this field is
 * the sharpest one in either round.** Asked for AURORA's SoundCloud, round 199's guess lane offers
 * `soundcloud.com/aurora` ("aurora") and `soundcloud.com/auroramusic` ("AuroraMusic") - both real
 * profiles, both belonging to strangers, both passing the shares-a-word check because the strangers took
 * the artist's own name as their handle - and it cannot generate `auroraaksnes`, which is hers.
 * **Verification proves a handle is REAL; it can never prove it is THEIRS.** That is round 199's own
 * YouTube sentence - existence is not identity - pointed back at the lane it shipped, and it is why a
 * `catalog` candidate and a `guess` candidate must never look alike on screen.
 *
 * - `connection` - an account this artist connected. Nothing guessed, nothing asked, no request made.
 * - `catalog` - MusicBrainz's own `url-rels` for the artist: links the artist (or an editor) DECLARED.
 * - `vendor` - a store's own artist index answering a name search (Apple, Spotify).
 * - `guess` - a handle built from the name and proved to resolve (SoundCloud, TikTok).
 */
export const PROFILE_SOURCES = ["connection", "catalog", "vendor", "guess"] as const;

export type ProfileSource = (typeof PROFILE_SOURCES)[number];

/**
 * One profile the artist might be. ⚠ **A CANDIDATE, never a write** (round 111's rule): the artist
 * reads a list of their own name and clicks themselves, which is the only shape in which a fuzzy match
 * is safe. `imageUrl` is null on the Apple lane because Apple ships no artist artwork at all.
 */
export interface ProfileCandidate {
	service: ProfileService;
	id: string;
	name: string;
	url: string;
	imageUrl: string | null;
	/** A short qualifier that helps tell two same-named artists apart - Apple's primary genre */
	detail: string | null;
	/** How much the app can vouch for this row - the panel says one sentence per source present */
	source: ProfileSource;
}

/**
 * The services an ARTIST ROW can be created from - the omnibox behind `<gh-artist-finder>` (2026-08-07).
 *
 * The owner's ask: *"I also want the user to be able to find themselves via Apple Music or SoundCloud
 * instead of just Spotify"*, and then *"it should be ONE field, an omnibox, that searches all three,
 * deduplicates matches and shows the results in one dropdown."*
 *
 * ⚠ **This is a SUBSET of `PROFILE_SERVICES`, and the four that are missing are missing on purpose.**
 * The artist editor's Social links panel searches all eight, one service at a time, to fill ONE FIELD on
 * an artist that already exists. This list creates the artist: the pick decides the row's NAME and its
 * AVATAR, so a wrong pick here is not a wrong link, it is the wrong person on every press kit the app
 * will ever render. Two things follow, and both point the same way:
 *
 * - **These four are where a name search identifies a RECORDING ARTIST.** Spotify, Apple and Deezer are
 *   fed by the artist's own distributor, so they know somebody who released their first single last month
 *   (`services/artistSearch.ts`'s WHO THIS APP'S ARTIST IS table); Instagram, TikTok, YouTube and Facebook
 *   identify a person with a handle, which is a different question with the same answer shape.
 * - ⚠ **The four that are out are the guess-and-verify lanes**, and `services/artistLinks.ts` measured
 *   what that costs: for AURORA the guess lane offers two strangers who took her name as their handle,
 *   both passing its relatedness check. Filling a FIELD with one of those is a bad link the owner can see
 *   and delete. NAMING THE ARTIST with one is a stranger's photograph on their own bio page.
 *
 * ⚠ **The ORDER is the authority ladder** `applyArtistOnboard` walks: whichever of these answers FIRST
 * decides the stored name, and the first to carry a photograph decides the avatar. Spotify and Apple carry
 * the name as the distributor delivered it to the stores; a SoundCloud display name is self-chosen and
 * routinely carries decoration ("NOVA REED music"), which is why it is last rather than because it is
 * worse evidence of identity.
 */
export const ONBOARD_SERVICES = ["spotify", "apple_music", "deezer", "soundcloud"] as const satisfies readonly ProfileService[];

export type OnboardService = (typeof ONBOARD_SERVICES)[number];

/**
 * One service a merged candidate was found on. ⚠ **`id` is the VENDOR's id (or a profile URL for a
 * catalog-sourced row), never a name** - the server re-reads the profile from it rather than trusting
 * anything the client sends, which is round 111's rule and the reason the wire carries so little.
 */
export interface OnboardPick {
	service: OnboardService;
	id: string;
	/** The profile URL, so a row can offer "open this and check it is you" without a second lookup */
	url: string;
}

/**
 * ONE ARTIST in the finder's dropdown, however many services found them.
 *
 * ⚠ **The merge is by folded name and it can be WRONG in one direction, so it is built to be wrong in the
 * other.** Two genuinely different artists do share a name, and merging them offers somebody a stranger's
 * Apple page under their own; so the merge only fires where every service is offering exactly ONE artist
 * under that name, and anything ambiguous stays as separate rows. `docs/notes/metadata-prefill.md`'s
 * governing rule: declining is free, a confident wrong answer is not. See `mergeByName`.
 */
export interface OnboardCandidate {
	/** Stable within one search - what the element keys its "which row is applying" state on */
	key: string;
	name: string;
	/** Whatever the services offered to tell two same-named artists apart, joined */
	detail: string | null;
	/** The best photograph any of the picks carried, in `ONBOARD_SERVICES` order */
	imageUrl: string | null;
	picks: OnboardPick[];
}

/**
 * What one omnibox search produced.
 *
 * ⚠ **`failed` exists because an empty list is not one fact** - round 189's absence-versus-outage split,
 * which `ProfileSearchResult.matchedArtists` makes one lane over for the same reason. "Apple has nobody by
 * that name" and "Apple did not answer" look identical on screen and are not the same problem, and only
 * one of them is worth re-typing a name over.
 */
export interface OnboardSearchResult {
	candidates: OnboardCandidate[];
	/** The services this install actually asked - so the UI never implies it searched one it cannot */
	searched: OnboardService[];
	failed: OnboardService[];
}

/** Whether one onboard service can be searched AND re-read on this install - they are the same seam */
export interface OnboardServiceStatus {
	service: OnboardService;
	searchable: boolean;
}

/**
 * HOW an artist's Spotify profile was decided - §AF, the resolver's confidence ladder.
 *
 * ⚠ **This is the field that makes the whole lane honest, and it exists because the risk here is not
 * "we found nothing" - it is CONFIDENT WRONGNESS.** A Spotify link is not a decoration: it is what
 * `socialIngest` snapshots against, what the bio page turns into a Listen button, and what an editor
 * clicks off a press kit. A stranger's profile there is a stranger's music offered as the artist's own,
 * under their name, to people they are trying to impress. So a resolution travels with the rung that
 * produced it, and the caller decides what that rung is good enough FOR.
 *
 * The order is the brief's own (`Spotify API Alternative Research - Implementation Brief.md`,
 * 2026-08-21), with the rungs this app can actually reach kept and the rest dropped:
 *
 * ⚠ **The order is by what FEEDS the source, not by how good the source is** (the owner, 2026-08-22:
 * *"the users of our app won't be on MusicBrainz... they won't have such artists"*). Everything from
 * `urlRel` down is a volunteer-edited encyclopedia and **should be expected to miss** for a small
 * independent artist; the two rungs above it rest on something the artist themselves handed us.
 *
 * - `stored` - the artist row already carries it. Nothing was asked and nothing was guessed.
 * - `albumPage` - a Spotify ALBUM link the owner pasted for their own release names its artist, in a
 *   public page's Open Graph tags. ⚠ **No catalog anywhere in it**, and `releases.spotify_album_id` is
 *   a column this app has had since v51 precisely because the owner copies that URI out of Spotify for
 *   Artists *before release day* - so it is the one rung that fires for an artist with one unreleased
 *   single and no wiki entry, which is exactly who this app is for.
 * - `youtubeLinks` - the artist's OWN YouTube channel declares a Spotify artist page **and** a Deezer or
 *   Apple artist page this workspace already holds. ⚠ **Artist-fed, so it fires on day one**: nobody has
 *   to have registered them anywhere, they just have to have linked their own profiles - which is what
 *   an artist promoting a release does before anything else. The shared platform id is what proves the
 *   channel is theirs; a channel that only shares the NAME resolves as `name` and is never written.
 * - `urlRel` - the catalog is asked **who owns the Deezer or Apple page we already store**, and answers
 *   with the artist, who declares a Spotify page. ⚠ **No name search anywhere in it** - this is the
 *   strongest and cheapest rung and it went in one round late, after `crossLink` had already reached
 *   the same evidence the expensive way round.
 * - `isrc` - an ISRC this workspace already holds names exactly one recording artist in the catalog,
 *   and that artist declares a Spotify page. Every hop is keyed; the brief's rung 1.
 * - `crossLink` - a catalog artist who declares the SAME Deezer or Apple artist page this app already
 *   stores for them. ⚠ **Identity proven by a shared platform id rather than by a name** - the brief's
 *   rung 3, and the free one that solves the case the owner actually hit.
 * - `overlap` - a catalog artist whose own recordings share an ISRC, or whose releases share a barcode,
 *   with something this workspace already holds. The brief's rung 5, and its worked example: *two
 *   artists called "Home"; the one carrying both of our ISRCs is ours and the other is rejected.*
 * - `name` - a name match and nothing else. ⚠ **Never written automatically.** It is offered to the
 *   artist as a candidate they pick, which is the one condition round 111 says makes a fuzzy answer
 *   safe, and it is exactly the rung the brief says to stop trusting.
 * - `vendor` - a paid cross-platform resolver answered. Dark today; see `services/spotifyResolve.ts`.
 *
 * ⚠ **It is a KEY, never a rendered sentence.** A payload field carrying prose is the class-3 i18n
 * blind spot `docs/notes/text-and-locale.md` names: no scanner can see it and every reader gets
 * English. The UI maps this union to a `t()` key with a literal string.
 */
export const SPOTIFY_RESOLUTION_RUNGS = ["stored", "albumPage", "youtubeLinks", "urlRel", "isrc", "crossLink", "overlap", "name", "vendor"] as const;

export type SpotifyResolutionRung = (typeof SPOTIFY_RESOLUTION_RUNGS)[number];

/**
 * The rungs whose evidence is an EXACT KEY or a SHARED PLATFORM ID, so the app may write them into
 * `social_links.spotify` without anybody looking at them.
 *
 * ⚠ **A named list rather than a comparison against the union's index, because "high enough" is not a
 * POSITION - it is a question about what identified the candidate.** `name` is excluded not for sitting
 * fifth but because nothing except a string comparison stands behind it, and the stranger a string
 * comparison cannot filter is the one squatting on the artist's own name (`artistLinks.ts`'s AURORA
 * measurement). Reading this as a threshold is how a future rung gets admitted by where it was written.
 *
 * ⚠ **`vendor` IS here, and the reason is a restriction on the seam rather than trust in a vendor**:
 * `services/spotifyResolve.ts` may only hand a paid resolver a URL this app already holds and ask for
 * the same entity on Spotify, never a name to search. That makes a vendor answer the identical
 * assertion `crossLink` gets from MusicBrainz - two platform ids naming one artist - so admitting one
 * and refusing the other would be paying for evidence and then declining to use it. **If a future round
 * teaches the seam to search a NAME, this list is what has to change in the same commit.**
 */
export const SPOTIFY_AUTOFILL_RUNGS: readonly SpotifyResolutionRung[] = [
	"stored",
	"albumPage",
	"youtubeLinks",
	"urlRel",
	"isrc",
	"crossLink",
	"overlap",
	"vendor"
];

/**
 * One answer to *which Spotify artist is this artist* - `services/spotifyResolve.ts`.
 *
 * ⚠ **The URL is the product and the id is derived from it, not the other way round.** Nothing in this
 * app reads a Spotify TRACK id and nothing needs a Spotify catalog crawl; what a release, a bio page
 * and the follower snapshot all need is one profile URL in `social_links.spotify`. The brief proposed a
 * whole `SpotifyProvider` with `searchArtist`/`getArtistReleases`/`getReleaseTracks` behind a paid
 * vendor - **that re-creates the Spotify API as a bill for capabilities this repo already replaced for
 * free**, so the seam kept the one method that answers a question the app actually asks.
 */
export interface SpotifyResolution {
	/** The canonical `https://open.spotify.com/artist/<id>` URL - what `social_links.spotify` stores */
	url: string;
	/** The base-62 artist id, parsed out of the URL. Present because three callers would otherwise re-parse it */
	artistId: string;
	rung: SpotifyResolutionRung;
	/**
	 * What the rung was proved AGAINST, for the operator's benefit and for the owner's.
	 *
	 * ⚠ **A derived choice nobody can see is indistinguishable from the app ignoring what the owner just
	 * did** (round 197's rule, and §AF's own governing one). "We filled your Spotify link" is not a
	 * sentence anybody should have to take on faith about a link that goes on their press kit, so the
	 * resolution says *which* Deezer page, *which* ISRC, *which* catalog artist agreed - a wire value the
	 * UI renders, never a sentence the server writes.
	 */
	evidence: string | null;
	/** The catalog artist this came through, where there was one - so an operator can check a bad answer */
	catalogArtist: string | null;
	/**
	 * ⚠ **Spotify's own keyless oEmbed confirmed this page is LIVE and said what it is called.**
	 *
	 * A catalog relation can point at a profile that has since been deleted or merged, and this value
	 * goes onto a press kit an editor clicks - so every resolution is checked against the platform
	 * itself before it may be written, for one free request with no credentials. **A 404 drops the
	 * answer entirely**; this being `false` means only that we could not ask (see
	 * `services/spotifyOEmbed.ts` on why a malformed id answers 504 rather than 404, and why
	 * collapsing those would let a bad minute at Spotify's edge delete a good link).
	 *
	 * ⚠ **It does NOT promote a rung.** Verification proves a profile is real and can never prove it is
	 * THEIRS - `artistLinks.ts` paid an AURORA measurement for that sentence - so a `name` match whose
	 * title oEmbed happily confirms is still a coin flip between two same-named artists.
	 */
	verified: boolean;
	/** The page's own title, from oEmbed - the name Spotify shows, which is not always the catalog's */
	spotifyName: string | null;
	/** Spotify's own image for the page. On a keyless install this is one of only two lanes with a face */
	imageUrl: string | null;
}

/**
 * The public catalogs this app will ask about a release (§S sources 2 and 3).
 *
 * ⚠ **The ORDER is the confidence ladder `services/catalogLookup.ts` applies**, not an alphabetical
 * list: whoever is asked first wins a field and everyone after may only fill a blank. MusicBrainz is
 * a curated catalog with a real label field; Apple is a store whose only answer to "who released
 * this" is a `℗` line. A round that adds a fourth source has to decide where in this list it belongs
 * before it can add it, which is the point of spelling it as one array.
 *
 * ⚠ **Round 201 put Deezer SECOND, above Apple, and the reason is a field rather than a preference.**
 * Deezer's album object carries a real `label` string ("Universal Music Ltda.", and on a self-release
 * the artist's own name); Apple's only answer to who put a record out is a `℗` copyright line that
 * `labelFromCopyright` has to parse out of a sentence. A catalog with the field beats a store with a
 * convention. It stays BELOW MusicBrainz because MusicBrainz is the only one of the three with
 * relationship credits at all - Deezer's `contributors` are `Main` and `Featured` and nothing else, so
 * it can never answer who produced or mixed the record.
 */
export const CATALOG_VENDORS = ["musicbrainz", "deezer", "itunes"] as const;

export type CatalogVendor = (typeof CATALOG_VENDORS)[number];

/**
 * Where a prefilled value came from (§S, round 196).
 *
 * ⚠ **The lane's rule is that every prefilled field must SAY where it came from**, and round 195
 * could satisfy that with one sentence because there was one source. A second source makes the
 * attribution a per-FIELD fact rather than a per-NOTE one: a note that says "from the file's own
 * tag" over a label that came off MusicBrainz is a worse lie than saying nothing, because the owner
 * trusts the tag (their distributor wrote it) more than a public catalog anyone can edit.
 *
 * ⚠ **It is DERIVED from `CATALOG_VENDORS` rather than restated**, which is round 187's `UI_LOCALES`
 * shape: a source is the file's own tag or it is one of the catalogs, and a hand-written union is a
 * place where a vendor exists to the lookup and not to the note that attributes it.
 */
export const PREFILL_SOURCES = ["tag", ...CATALOG_VENDORS] as const;

export type PrefillSource = (typeof PREFILL_SOURCES)[number];

/**
 * ⚠ **The cover is a prefill target and is NOT a `TagField`**, and the two facts are not in tension.
 * `TAG_FIELDS` is the list of columns a suggestion payload may name, and the cover stays out of it
 * for the reason written above it - hundreds of kilobytes of base64 have no business on the fast
 * upload response. The lookup response is the slow, opt-in one, so it can afford to carry an image;
 * what it may not do is pretend the image is an ordinary field.
 */
export type PrefillField = TagField | "cover";

/**
 * HOW the record a value came from was identified - round 209, filed by round 201.
 *
 * ⚠ **The source says WHO answered; the path says how sure we are that they answered about THIS
 * record**, and only the second one tells the owner whether a value deserves a second look. Every
 * source in this lane can be reached two ways: `"key"` is a code somebody's distributor stamped (an
 * ISRC, a barcode) or a record the artist pointed at themselves, and `"match"` is round 201's bridge -
 * the artist's name resolved to their own shelf and the title matched inside it, which is an
 * INFERENCE however narrow its declines are.
 *
 * ⚠ It rides on the MARK rather than on the note, for `CatalogSuggestion.source`'s reason one level
 * over: an editor accumulates marks from more than one prefill (the upload's tag, then a lookup), so a
 * per-note path could only ever be right about the last one.
 */
export const PREFILL_PATHS = ["key", "match"] as const;

export type PrefillPath = (typeof PREFILL_PATHS)[number];

/** One filled field, the source that filled it, and how that record was identified */
export interface PrefillMark {
	field: PrefillField;
	source: PrefillSource;
	path: PrefillPath;
}

/**
 * One field a catalog can fill, and WHICH catalog said so.
 *
 * ⚠ **The source rides on the SUGGESTION, not on the response**, and round 198 is where that stopped
 * being a style choice: two catalogs answer one lookup, so a per-response source could only have been
 * right about one of them. A batch-level "this came from MusicBrainz" over a genre that came from
 * Apple is the exact lie `PREFILL_SOURCES` exists to prevent, one level up.
 */
export interface CatalogSuggestion extends TagSuggestion {
	source: CatalogVendor;
	/** Whether the record this came from was identified by a KEY or matched by name - see `PrefillPath` */
	path: PrefillPath;
}

/**
 * `GET /api/audio/:id/lookup` and `GET /api/releases/:id/lookup` - what the public catalogs know
 * about a release, keyed EXACTLY off its ISRC or barcode (§S rounds 196 and 198).
 *
 * ⚠ **An empty `suggestions` list is a DECLINE and it is the ordinary case, not an error.** A release
 * with no ISRC and no barcode has no exact key, and this lane never matches on a title and an artist
 * name - "Nova Reed - Midnight Static" resolves to several recordings and picking one fills the form
 * with a different record's credits. The caller renders nothing and says nothing.
 */
export interface CatalogLookupResult {
	/** Blank-only suggestions in `TAG_FIELDS` order, each naming its own catalog, never a write */
	suggestions: CatalogSuggestion[];
	/** Front cover as a data URL: the file's OWN embedded art first, then a catalog's */
	cover: string | null;
	/** Which source supplied the cover, so the note can attribute it */
	cover_source: PrefillSource | null;
	/** …and how that record was identified, so a cover found by NAME can say so like every other field */
	cover_path: PrefillPath | null;
}

/**
 * The ARTIST columns an autofill may fill - §X, round 202.
 *
 * ⚠ **The list is short because most of what an artist row holds is not fetchable, and two absences are
 * decisions rather than gaps.** `pronouns` is missing on purpose: MusicBrainz ships a `gender` field and
 * it is not the same thing, an editor typed it about somebody else, and being wrong there misgenders the
 * person using the app rather than producing a bad press kit. `artist_press_highlights` is missing
 * because a press quote is somebody else's sentence about the artist - fabricating or scraping one is
 * the confident wrongness §S spends every round refusing, at the field where it would be least
 * checkable.
 *
 * ⚠ **The ORDER is the reading order of the note the editor renders**, exactly like `TAG_FIELDS`.
 */
export const ARTIST_AUTOFILL_FIELDS = ["artist_bio", "artist_bio_short", "artist_origin", "current_location", "avatar_image"] as const;

export type ArtistAutofillField = (typeof ARTIST_AUTOFILL_FIELDS)[number];

/**
 * Where an autofilled artist value came from.
 *
 * ⚠ **`connection` outranks everything and `ai` is last, and that ordering is the round's whole design.**
 * A connected account's bio is the artist's OWN WORDS - the copy-paste the owner asked to be spared -
 * while an AI draft is a first sentence to edit. **A bio is voice, not a fact**, so the note must be able
 * to say which of the two the owner is looking at; a single "autofilled" flag over both would be the lie
 * `PREFILL_SOURCES` exists to prevent one lane over.
 *
 * ⚠ **There is no `wikipedia`, deliberately.** It is one hop away through MusicBrainz's `url-rels` and it
 * is a stranger's CC BY-SA prose describing the artist in the third person. **Reachable is not the same
 * as ours to use.**
 */
export const ARTIST_AUTOFILL_SOURCES = ["connection", "musicbrainz", "deezer", "ai"] as const;

export type ArtistAutofillSource = (typeof ARTIST_AUTOFILL_SOURCES)[number];

/** One artist field an autofill can fill, and who said so - never a write, always a suggestion */
export interface ArtistAutofillSuggestion {
	field: ArtistAutofillField;
	value: string;
	source: ArtistAutofillSource;
}

/**
 * `GET /api/artists/:id/autofill` - what the app can honestly learn about one artist.
 *
 * ⚠ **An empty `suggestions` list is the ORDINARY answer for a brand-new artist with nothing connected**,
 * not an error. The caller renders nothing and says nothing, exactly like the release lane.
 */
export interface ArtistAutofillResult {
	suggestions: ArtistAutofillSuggestion[];
	/**
	 * Whether a bio DRAFT is worth offering - a question about the evidence, not about the AI.
	 * ⚠ With no catalog facts and no releases, a model has only the name to write from, and what comes
	 * back is the paragraph of adjectives this lane exists to keep off a press kit.
	 */
	canDraftBio: boolean;
}

// ── Support (§T round 1, round 210) ──────────────────────────────────────────

/**
 * What a ticket is ABOUT, as a closed list.
 *
 * ⚠ Five buckets and no "other" that means everything: a kind exists to route a thread to whoever
 * should read it and to let the queue be triaged at a glance, and a free-text category does neither.
 * `other` is here because refusing it makes people pick a wrong one, which is worse than a bucket
 * that honestly says "this does not fit".
 *
 * ⚠ **`feature` was added on the owner's ask (2026-08-07) and it is not a fifth flavour of bug.** A
 * wish is the one thing on this list that is not a report of something WRONG, and without a bucket
 * for it people file it as `bug` (where it reads as a defect nobody can reproduce) or as `other`
 * (where the queue learns nothing). It also changes what Help IS: a desk that only takes faults
 * collects only faults, and the tenant who has an idea about the product goes quiet instead - which
 * is §T's own governing finding, a failure whose only symptom is silence.
 */
export const SUPPORT_TICKET_KINDS = ["bug", "question", "feature", "billing", "other"] as const;

export type SupportTicketKind = (typeof SUPPORT_TICKET_KINDS)[number];

/**
 * ⚠ THREE states and no "waiting on customer": `answered` means we wrote back, and it flips to `open`
 * again the moment they reply. A fourth state that only a staff member can set is a state that goes
 * stale silently - the customer's own next message is the only honest evidence about who the thread
 * is waiting on.
 */
export const SUPPORT_TICKET_STATUSES = ["open", "answered", "closed"] as const;

export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

/**
 * The screenshot limits, shared so the picker and the route cannot disagree about them (owner ask,
 * 2026-08-07: "also allow for screenshots").
 *
 * ⚠ **The bound is the point, not the feature.** The lane note filed attachments as "the single most
 * useful thing a bug report carries and also unbounded bytes a stranger uploads - size it
 * deliberately or decline it deliberately". This is the deliberate size: images only, four of them,
 * 5MB each, which is a phone screenshot with room to spare and nowhere near a video.
 *
 * ⚠ **The frontend checking these is a KINDNESS, never the guard.** The route re-checks every one of
 * them against the bytes that actually arrived, because a caller who does not use our picker is the
 * only caller that matters here.
 */
export const SUPPORT_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
export const SUPPORT_SCREENSHOT_MAX_PER_MESSAGE = 4;
/**
 * ⚠ **An allowlist of formats we can prove from the BYTES, not a list of things a browser might
 * send.** A `Content-Type` header is whatever the caller typed; `sniffImage()` in
 * `services/support.ts` reads the magic number instead, so this list is exactly the set of magic
 * numbers it knows. SVG is deliberately absent: it is a script host, and it would be served back to
 * a staff member from the operator's own origin.
 */
export const SUPPORT_SCREENSHOT_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"] as const;
export type SupportScreenshotType = (typeof SUPPORT_SCREENSHOT_TYPES)[number];

/** One image hung off a message - the report's screenshot, or one attached to a later reply */
export interface SupportAttachment {
	id: number;
	messageId: number;
	/** What the file was called on their machine, kept only so a staff member can be told which is which */
	filename: string;
	mime: SupportScreenshotType;
	bytes: number;
}

/**
 * Who wrote one message in a thread.
 *
 * ⚠ **`ai` is a THIRD author and never a flavour of `staff`** (§T round 3, 2026-08-07). The obvious
 * cheaper shape - write the robot's answer as a `staff` message with a fake address - is the one
 * thing this feature may not do: the tenant's screen says "From us" over a `staff` message, and a
 * person who cannot tell whether they are talking to a human is a person who cannot decide whether
 * to ask for one. It is also load-bearing for the caps below, which are DERIVED by counting
 * `author = 'ai'` rows, and for the guard that the AI answers a TENANT message only - both of which
 * need to be able to tell the robot's own words apart from everybody else's.
 */
export type SupportMessageAuthor = "tenant" | "staff" | "ai";

/**
 * What travels down the live support socket - the ONE wire format, shared by three programs.
 *
 * It lives in `shared/` because it has three readers that cannot be changed together: the server
 * (`services/supportSocket.ts`), the tenant app's Help chat, and Crew's client bundle. Two of those
 * are separate browser bundles built by separate esbuild invocations, so a hand-mirrored copy would
 * typecheck green on all three sides while one of them read `undefined` off every field - which is
 * `src/frontend/CLAUDE.md`'s standing finding about mirrored payloads, in its worst configuration
 * yet, because here the mirrors would live in different tsconfig projects.
 *
 * ⚠ **It carries no message BODY, and that is a rule rather than an omission.** The socket says
 * *thread N changed*; the client then re-fetches the thread through the ordinary authenticated
 * route. That route is where the read is authorized, where attachments resolve, and where the
 * thread's own rules apply (hidden, closed, `deletable`). Putting the text on the wire would be a
 * second way to read a conversation, with none of that behind it - and it would be the copy with no
 * tests on it. The fields here exist so a client can decide whether it CARES (is this my open
 * thread? did a human write it, or the assistant?), never so it can render without asking.
 */
/**
 * The path both processes serve the live support socket on.
 *
 * ⚠ In `shared/` rather than beside the server that mounts it, because THREE programs need it and two
 * of them are browser bundles that cannot import `src/services/`. A hardcoded second copy in a client
 * is a lane that silently never connects - the socket refuses, the browser reports a close, and the
 * reconnect loop retries a wrong path forever while every page looks fine.
 */
export const SUPPORT_SOCKET_PATH = "/ws/support";

/**
 * A support thread changed. ⚠ **It carries no BODY** - the client re-fetches through the ordinary
 * route, which is the one place the read is authorized and a thread's own rules apply.
 */
export interface SupportMessageFrame {
	type: "support.message";
	ticketId: number;
	messageId: number;
	author: SupportMessageAuthor;
	createdAt: string;
}

/**
 * A piece of an answer the assistant is still writing (round 215).
 *
 * ⚠ **This is the ONE frame in this lane that carries text, and the rule above is not broken by it -
 * it is what makes the exception safe.** A `support.message` frame refers to a row that EXISTS, so
 * sending its body would be a second, untested way to read a stored conversation. A delta refers to
 * nothing: there is no row yet, no id to fetch, and no other way to see it. When the answer lands it
 * arrives as an ordinary `support.message` and the client re-fetches exactly as before, discarding
 * whatever it previewed.
 *
 * ⚠ **So a stream frame is never authoritative.** A reader that stored one, or counted it, or marked
 * a thread read on one, would be treating a draft as a message - the answer may still be discarded by
 * the verdict, the write may fail, and nothing here has been through `ticketById`.
 */
export interface SupportStreamFrame {
	type: "support.stream";
	ticketId: number;
	delta: string;
}

/**
 * The other side of this thread is composing right now.
 *
 * ⚠ **`who` is what stops a client seeing its own reflection.** Both sides of a conversation push
 * these onto the same lane, and a tenant's socket hears every frame for their workspace - including
 * the ones their own keystrokes just caused. Filtering on the SIDE is structural; filtering on "was
 * it me" would need a client identity the socket deliberately does not carry.
 *
 * ⚠ **`typing: false` is sent exactly once, by the server**, when a heartbeat lapses. A client that
 * decided this for itself would need its own timer and its own copy of the freshness window, and the
 * two copies would drift the first time either number changed - see `services/supportTyping.ts`.
 */
export interface SupportTypingFrame {
	type: "support.typing";
	ticketId: number;
	who: "tenant" | "staff";
	typing: boolean;
}

export type SupportLiveFrame = SupportMessageFrame | SupportStreamFrame | SupportTypingFrame;

export interface SupportTicketMessage {
	id: number;
	author: SupportMessageAuthor;
	/** Who answered, when a staff member did - the tenant sees the operator's address, not a name */
	staffEmail: string | null;
	body: string;
	createdAt: string;
	attachments: SupportAttachment[];
}

export interface SupportTicket {
	id: number;
	workspaceId: number;
	kind: SupportTicketKind;
	/**
	 * The queue's scannable label, DERIVED from the first message rather than typed (owner ask,
	 * 2026-08-07: "the One line is unnecessary - just a text box"). See `deriveSubject()`.
	 */
	subject: string;
	status: SupportTicketStatus;
	/** JSON the SERVER composed about the app (edition, version, page) - never about the person */
	context: string | null;
	createdAt: string;
	updatedAt: string;
	closedAt: string | null;
	/** When the tenant last had this thread open in front of them - NULL until they ever have */
	readAt: string | null;
	/**
	 * When this thread became a HUMAN's to answer - NULL while the AI assistant is handling it.
	 *
	 * ⚠ **A second axis, not a fourth status** (v129). `status` answers *who is waiting*; this answers
	 * *is a person needed*, and the two are independent - an `answered` thread can want a human and an
	 * `open` one can be perfectly served by the robot. Folding them together would break the reopen
	 * rule, since a tenant's reply flips `answered` back to `open` and must NOT flip a thread out of
	 * the human queue it was escalated into.
	 *
	 * ⚠ It is also what the tenant's screen reads to decide whether to offer "Talk to a person": the
	 * control is visible at all times while this is NULL, never earned and never behind a "did that
	 * answer your question?" - being trapped in a bot is the failure mode of every product that has
	 * ever shipped one.
	 */
	escalatedAt: string | null;
	/**
	 * Who started this conversation (v130). ⚠ **A different axis from `kind`**, deliberately: a sales
	 * outreach is about billing, or a feature, or nothing on that list, and folding "who started it"
	 * into "what is it about" costs the queue its triage while answering a question nobody asked.
	 */
	openedBy: "tenant" | "staff";
	/**
	 * When the TENANT tidied this thread out of their main list - NULL while it is in it (v131).
	 *
	 * ⚠ **Hiding is a tidying gesture, not a mute.** Nothing that counts unread answers reads this
	 * column, and nothing in Crew reads it at all: a hidden thread that swallowed a reply is a
	 * customer nobody answered, which is the failure this whole feature exists to end.
	 */
	hiddenAt: string | null;
	/**
	 * May the tenant DELETE this thread outright - derived, never stored (owner, 2026-08-07: *"ONLY if
	 * human staff has never responded to it"*).
	 *
	 * ⚠ **An `ai` message does NOT block it, and that is the one judgment call in the rule.** The
	 * assistant is not a person: a thread where somebody asked a robot a question and got an answer
	 * out of the documentation is entirely their own, and nobody at the operator has said anything in
	 * it that they have an interest in keeping. A HUMAN's words are different - half the conversation
	 * is then the operator's, said in the operator's name - so a staff reply, or a thread staff
	 * started, makes it hidable and not deletable.
	 */
	deletable: boolean;
	/**
	 * Whether an answer has landed that the tenant has not seen yet - the whole basis of the in-app
	 * notification, and DERIVED on every read rather than stored (`attentionRows()`'s rule: a row that
	 * costs no table and no dismissal cannot go stale).
	 */
	unread: boolean;
	messages: SupportTicketMessage[];
}

/** `GET /api/support/tickets` - the tenant's own threads, plus whether this install has support at all */
export interface SupportOverview {
	/** False in selfhost, where there is nobody to file a ticket TO - see `services/support.ts` */
	enabled: boolean;
	/**
	 * Whether the assistant can actually answer first (§T round 3), i.e. whether the OPERATOR has an
	 * AI lane configured at all - `supportAiAvailable()`.
	 *
	 * ⚠ **It is here because the panel was PROMISING an assistant that could not exist** (owner,
	 * 2026-08-09, on finding the deployed box has no AI credentials). The server has always done the
	 * right thing - `answerOrEscalate` files the ticket and hands it straight to a person - but the
	 * new-conversation form said *"An assistant answers first, using the product documentation"*
	 * unconditionally, because this payload carried nothing to condition it on. **A correct backend
	 * and a lying front end is still a lie**, and this is the shape it takes: the failure is invisible
	 * to every server test, because the server behaved perfectly the whole time.
	 *
	 * ⚠ It is the OPERATOR's fact, never the reader's: it says nothing about this workspace's budget.
	 * Folding a tenant's spent allowance in here would turn one customer's exhausted plan into "we
	 * have no assistant", which is a different sentence and a false one.
	 */
	aiAssist: boolean;
	tickets: SupportTicket[];
}

/**
 * The record the Get-started hero draws, when there is one (owner ask, 2026-08-07: *"use real data in
 * the right illustration just like the landing page, and drive it from the same logic in Crew"*).
 *
 * ⚠ **It is the SHOWCASE - the same record the public landing page advertises**, resolved by
 * `services/showcase.ts` and chosen by an admin in Crew at `/showcase`. That is what settles
 * the obvious objection, which is worth writing down because the next reader will raise it: showing
 * one workspace's release inside another workspace's app sounds like a leak, and this one is not.
 * The record is workspace 1's, it is live on the hub, it is past its release date, and a person
 * deliberately put it on the front door of the internet - `showcase()` re-checks all three on
 * every read, so a record that stops being public stops being drawn. Nothing here is derived from
 * the reader's own workspace and nothing about a customer crosses.
 *
 * ⚠ **`null` is the ordinary answer, not an error.** See `servedShowcase()` for what has to be true
 * before this is non-null; the hero draws its own gradient otherwise, because a broken image on the
 * app's first screen is worse than a drawing.
 *
 * ⚠ **Every field here is a whole ARM of the drawing, so a missing one is a gradient rather than an
 * error** (§Z round 2). `catalog` and `portrait` were added when the hero learned to draw more than
 * one record, and `portrait` had been travelling on the wire UNDECLARED since round 228 - the server
 * grew the field and this interface did not, which typechecks green on both sides while the client
 * cannot see it. That is round 195's hand-mirrored-payload trap arriving from the other direction:
 * the mirror is gone, but a payload's TYPE can still lag the payload.
 */
export interface OnboardingShowcase {
	artist: string;
	title: string;
	/** A URL THIS app serves - see `servedShowcase()`, which is what makes pointing an `<image>` at it safe */
	cover: string;
	/**
	 * The `HERO_COVER_COUNT`-tile sprite of the same artist's catalog - ONE file, three windows.
	 *
	 * ⚠ **It is a sprite rather than three URLs for the landing page's reason, and the hero inherits
	 * it**: three files are three round trips. A drawing shows one tile by clipping a window and
	 * shifting the whole image behind it.
	 */
	catalog: string;
	/**
	 * A real 9:16 image, or null - and null is still the ORDINARY answer, because most releases have
	 * no portrait crop and most installs have picked nothing.
	 *
	 * ⚠ **BOTH showcase arms can supply one** (round 232): the custom arm carries an uploaded 9:16,
	 * and the release arm reads the portrait crop the owner uploaded into `release_covers`. This said
	 * "only the custom arm" for two rounds, which was a claim about the schema the schema had stopped
	 * making.
	 *
	 * ⚠ **A caller with a portrait-shaped hole draws its own chrome for null and never reaches for
	 * `cover`.** A square sliced into a 9:16 frame is a face cut in half, which is the defect §Z was
	 * filed about; see `Showcase.portrait` in `services/showcase.ts`.
	 */
	portrait: string | null;
}

/**
 * `GET /api/onboarding` - the 🚀 Get started guide's whole state.
 *
 * ⚠ **It lives here rather than in `src/services/onboarding.ts`** because the guide's island used to
 * re-declare all six of its fields by hand, which is the shape round 195 paid for: two copies
 * typecheck green on both sides while the page renders `undefined` from every field one of them has
 * not grown yet. `AutopilotStatus` and `QuickstartDerived` are here for the identical reason.
 */
export interface OnboardingState {
	/** Show the 🚀 Get started nav item - not dismissed, and the artist or release step is still open */
	needed: boolean;
	/** A truly empty, undismissed install - the boot navigation lands on the guide instead of an empty dashboard */
	firstRun: boolean;
	dismissed: boolean;
	/** The first artist/release, as proof the step happened and a place for the guide to link to */
	artist: { id: number; name: string } | null;
	release: { id: number; title: string } | null;
	connections: {
		ai: { connected: boolean; detail: string | null };
		gmail: { appConfigured: boolean; authorized: boolean };
		/** Can this owner send mail AT ALL, and through which pipe - see the note at the assignment */
		mail: { connected: boolean; lane: MailLane | null };
		spotify: { configured: boolean };
		publishing: { netlifyConfigured: boolean; domain: string | null };
		/**
		 * Platform names connected FOR THE ARTIST NAMED IN `artist` ABOVE - the one step 1 shows and
		 * step 3's button opens. Not `defaultConnectionArtist()`: see the assignment's note.
		 */
		socials: { connected: string[] };
	};
	/** The record the hero illustration draws, or null to draw the gradient - see `OnboardingShowcase` */
	showcase: OnboardingShowcase | null;
}
